/**
 * O espacial da campanha CPTu na tela (onda 316, Tarefa 316.2; spec 07 §5.7 e §11) — contas puras, sem DOM:
 * quem entra, o pedido da seção, a seção recortada pelo terreno, as zonas estimadas, as isolinhas, as
 * classes da escala e a leitura de uma célula. O worker (`espacial.worker.ts`) chama `calcularSecao`; sem
 * worker, a página; na Tarefa 316.5, o servidor (a cópia em `api/src/cptu/visual/`). A conta de interpolar
 * é do motor (`../motor`, `espacial.ts`): aqui só se monta o pedido e se recorta a resposta.
 *
 * MODELO INTERPRETATIVO, FORA DO RN-252 (R1): entre as verticais o valor é ESTIMADO (RN-C07 da spec 02) —
 * a legenda fixa `LEGENDA_DO_ESPACIAL` vai em todo desenho. O que a tela escolheu sem fonte está na lista
 * `ESCOLHAS_DA_ANALISE` (R10), cada item com o id entre colchetes num comentário «ESCOLHA DA TELA, SEM
 * FONTE [esp.…]» ao lado do número no código — o molde das `cam.*` da 313. Nenhuma entra na conformidade.
 *
 * O que a tela decide (R4, R5, R6, R9):
 *  · entram as verticais ativas com latitude, longitude e cota; as outras vêm listadas com o motivo;
 *  · SBTn: a zona é a do classificador do motor (`zonaSbtn`) sobre Fr e Qtn ESTIMADOS, interpolados em log₁₀
 *    [esp.log-fr-qtn]; o Ic estimado dá a isolinha 2,60; Su só se pinta onde a zona estimada é argilosa;
 *  · o variograma é um por coluna interpolada, das verticais úteis da campanha inteira, com as cotas a cada
 *    0,25 m [esp.variograma-cotas] e 8 classes [esp.variograma-classes]; krigagem esférica de partida
 *    [esp.krigagem-partida]; sem ajuste, o IDW (potência 2) com a frase — nunca calado;
 *  · o recorte: nada acima da linha das bocas, abaixo da dos fundos, nem fora das estações
 *    [esp.recorte-estacoes] — extrapolar não é estimar;
 *  · os passos de partida pela contagem de colunas (400) e de células (60) [esp.secao-passos]
 *    [esp.grade-passos], o dobro no celular [esp.celular-dobro].
 *
 * O modelo 3D (Tarefa 316.3; R6, R8, R9): a grade de voxels do motor recortada pelo terreno — dentro do fecho
 * convexo das verticais úteis e entre as superfícies das bocas e dos fundos, por IDW em planta
 * [esp.superficies-idw] —, com o voxel centrado no nó da grade [esp.voxel-centro]; o terreno com a imagem
 * cobre a caixa das verticais com 30 m de margem [esp.terreno-margem]. Os buffers do three.js saem do
 * `geometria3d.ts` (puro, sem o three); o three.js só entra no `Modelo3d.tsx`, por `import('three')`. A fatia em
 * planta (`fatiaEmPlanta`, passos 6–10) é a grade numa cota, na cota do nó mais perto [esp.planta-no], para a seção
 * desenhar em planta quando o navegador não tem WebGL.
 *
 * As sobreposições das verticais (pedido do Humberto de 16/10/2026: «permita que seja ligado ou desligado os
 * gráficos referentes a cada vertical, com ajuste de transparência… qc, qt, fs, u2… e a classificação SBT ou
 * SBTn, mesmas cores do CPeT-IT»): `sobreposicoesDasVerticais` monta, de cada vertical, o perfil MEDIDO de uma
 * grandeza (ou a coluna das zonas SBT/SBTn do motor) em cota, com a escala da faixa. A seção 2D desenha cada uma
 * na estação da vertical; o 3D (Tarefa 316.3) e o PDF (316.5) reusam a mesma conta. A faixa tem 48 px
 * [esp.sobreposicao-largura], a opacidade de partida é 70 % [esp.sobreposicao-opacidade], a escala é a mesma em
 * todas as faixas de partida [esp.sobreposicao-escala] e a zona de cada leitura vale até o meio do caminho às
 * vizinhas [esp.sobreposicao-trecho]. Medido nunca se confunde com estimado: a faixa é a vertical, não a seção.
 */
import {
  ajustarVariograma, gamaDoModelo, gradeDeVoxels, idw, krigagemOrdinaria, planoLocal, secaoAoLongoDaLinha, variogramaDasCotas, variogramaExperimental,
  zonaArgilosa, zonaSbtn,
} from '../motor';
import type {
  ColunaDoMotor, GradeDeVoxels, Grandeza, ModeloDeVariograma, ParExperimental, PlanoLocal, PontoPlano, Resultado, Secao, Variograma, VerticalEspacial,
} from '../motor';
import { dominioDe, numeroBr } from './escalas';
import type { EscalaDoEixo } from './escalas';
import { combinarMarcas, marcaDaColuna } from './homologacao';
import type { MarcaDeHomologacao } from './homologacao';
import { indiceNaProfundidade } from './leitura';

/* ── A legenda e as escolhas ───────────────────────────────────────────── */

/** A legenda fixa de todo desenho espacial (RN-C07 da spec 02): vai na seção, no 3D, no topo da aba e no PDF. */
export const LEGENDA_DO_ESPACIAL = 'modelo interpretativo — entre as verticais o valor é estimado';

/** A evidência de toda escolha da tela (R10). */
const EVIDENCIA_DA_TELA = 'escolha da tela, sem fonte — modelo interpretativo, fora do RN-252' as const;

/** Uma escolha da tela sem fonte, rotulada (R10). */
export interface EscolhaDaAnalise {
  id: `esp.${string}`;
  valor: string;
  descricao: string;
  evidencia: typeof EVIDENCIA_DA_TELA;
}

/** O passo das cotas do variograma, m — ESCOLHA DA TELA, SEM FONTE [esp.variograma-cotas]. */
export const PASSO_DAS_COTAS_DO_VARIOGRAMA = 0.25;
/** As classes do variograma — ESCOLHA DA TELA, SEM FONTE [esp.variograma-classes]. */
export const CLASSES_DO_VARIOGRAMA = 8;
/** A potência do IDW quando a krigagem não serve (a do motor). */
const POTENCIA_DO_IDW = 2;
/** As séries dos passos de partida e os limites — ESCOLHA DA TELA, SEM FONTE [esp.secao-passos] [esp.grade-passos]. */
const PASSOS_DA_SECAO = [0.25, 0.5, 1, 2, 5] as const;
const COLUNAS_NO_MAXIMO = 400;
const PASSO_VERTICAL_DA_SECAO = 0.05;
export const PASSOS_DA_GRADE = [1, 2, 5, 10] as const;
const CELULAS_NO_MAXIMO = 60;
const PASSO_VERTICAL_DA_GRADE = 0.25;
/** No celular, o dobro dos passos — ESCOLHA DA TELA, SEM FONTE [esp.celular-dobro]. */
const FATOR_DO_CELULAR = 2;
/** Quantos voxels o 3D desenha no máximo — ESCOLHA DA TELA, SEM FONTE [esp.voxels-limite]. */
export const VOXELS_NO_MAXIMO = 250_000;
/** A potência do IDW das superfícies das bocas e dos fundos no 3D — ESCOLHA DA TELA, SEM FONTE [esp.superficies-idw]. */
export const POTENCIA_DAS_SUPERFICIES = 2;
/** A margem do terreno em volta das verticais, m — ESCOLHA DA TELA, SEM FONTE [esp.terreno-margem]. */
export const MARGEM_DO_TERRENO = 30;
/*
 * O voxel do 3D é centrado no nó da grade do motor — ESCOLHA DA TELA, SEM FONTE [esp.voxel-centro].
 * A malha do terreno tem o passo da grade em planta, ancorada no canto sudoeste da caixa das verticais (a
 * vertical num múltiplo do passo cai num vértice), e chega aos limites — ESCOLHA DA TELA, SEM FONTE
 * [esp.terreno-malha]. A imagem do satélite: o maior zoom até o 19 da Esri em que o lado maior não passa de
 * 1.024 px (`mapaGeotecnico/imagemDoTerreno.ts`) — ESCOLHA DA TELA, SEM FONTE [esp.satelite-imagem].
 * O giro do 3D: 15° por toque de seta, ×1,25 na distância por + e − — ESCOLHA DA TELA, SEM FONTE [esp.giro-passos].
 */
/** Quantas linhas de corte e quantos pontos por linha — ESCOLHA DA TELA, SEM FONTE [esp.linhas-limite]. */
export const LINHAS_NO_MAXIMO = 6;
export const PONTOS_POR_LINHA = { minimo: 2, maximo: 20 } as const;
/** A opacidade do satélite no 3D, de partida — ESCOLHA DA TELA, SEM FONTE [esp.satelite-opacidade]. */
export const OPACIDADE_DO_SATELITE = 0.7;
/** O exagero vertical do 3D, de partida — ESCOLHA DA TELA, SEM FONTE [esp.exagero]. */
export const EXAGERO_VERTICAL = 3;
/**
 * A fita do gráfico de cada vertical no 3D (o pedido do Humberto de 16/10): a largura toda é esta fração da menor
 * distância em planta entre dois lugares distintos, entre o mínimo e o máximo, m; com um lugar só, `semPar` —
 * ESCOLHA DA TELA, SEM FONTE [esp.fita-3d].
 */
export const FITA_DO_3D = Object.freeze({ fracao: 0.4, minimo: 1, maximo: 20, semPar: 5 });
/** O afastamento máximo de uma vertical à linha, m (spec §5.7; o padrão do motor). */
export const AFASTAMENTO_DA_LINHA = 30;
/** Dois lugares a menos disto são o mesmo (o `LUGAR_IGUAL` do motor, escolha 1 dele). */
const LUGAR_IGUAL = 0.01;
/** Folga de ponto flutuante. */
const FOLGA = 1e-9;
/** Folga das superfícies do 3D, guardadas em Float32 (≈ 2 µm de resolução a 20 m): 10 µm. */
export const FOLGA_DO_FLOAT32 = 1e-5;

/** As escolhas da tela sem fonte (R10) — o `303.44` confere cada id num comentário deste arquivo. */
export const ESCOLHAS_DA_ANALISE: readonly EscolhaDaAnalise[] = Object.freeze([
  { id: 'esp.variograma-cotas', valor: '0,25 m', descricao: 'o variograma experimental soma as cotas de 0,25 em 0,25 m (variogramaDasCotas)' },
  { id: 'esp.variograma-classes', valor: '8', descricao: 'o variograma experimental tem 8 classes de distância até hMax (metade da maior distância)' },
  { id: 'esp.log-fr-qtn', valor: 'log₁₀', descricao: 'no SBTn e no Su, Fr e Qtn são interpolados em log₁₀ e a zona é a do classificador do motor sobre os estimados' },
  { id: 'esp.krigagem-partida', valor: 'krigagem esférica', descricao: 'o método de partida é a krigagem ordinária com o variograma esférico; sem ajuste, o IDW (potência 2) com a frase' },
  { id: 'esp.secao-passos', valor: '0,25 · 0,5 · 1 · 2 · 5 m; até 400 colunas; vertical 0,05 m', descricao: 'o passo horizontal de partida é o menor da série em que as colunas do motor (⌊comprimento/passo⌋ + 1, mais a do fim) não passam de 400' },
  { id: 'esp.grade-passos', valor: '1 · 2 · 5 · 10 m; até 60 células; vertical 0,25 m', descricao: 'o passo da grade de partida é o menor da série em que as células do lado maior (⌈extensão/passo⌉) não passam de 60' },
  { id: 'esp.celular-dobro', valor: '× 2', descricao: 'no celular os passos de partida dobram' },
  { id: 'esp.voxels-limite', valor: '250.000', descricao: 'o 3D desenha no máximo 250.000 voxels; acima, a tela propõe o passo seguinte' },
  { id: 'esp.recorte-estacoes', valor: 'entre a primeira e a última estação', descricao: 'nada se pinta antes da primeira estação nem depois da última, acima da linha das bocas nem abaixo da dos fundos (interpoladas linearmente entre as estações)' },
  { id: 'esp.superficies-idw', valor: 'IDW, potência 2', descricao: 'no 3D, as superfícies das bocas e dos fundos saem por IDW em planta; voxel, fatia e cerca só dentro do fecho convexo' },
  { id: 'esp.terreno-margem', valor: '30 m', descricao: 'o terreno com a imagem cobre a caixa das verticais úteis com 30 m de margem' },
  { id: 'esp.linhas-limite', valor: '6 linhas, 2 a 20 pontos', descricao: 'a campanha grava até 6 linhas de corte, cada uma com 2 a 20 pontos' },
  { id: 'esp.satelite-opacidade', valor: '70 %', descricao: 'a opacidade do satélite no 3D é 70 % de partida (de 0 a 100 %)' },
  { id: 'esp.exagero', valor: '× 3', descricao: 'o exagero vertical do 3D é ×3 de partida (de ×1 a ×10)' },
  { id: 'esp.voxel-centro', valor: 'no nó', descricao: 'no 3D, cada voxel é centrado num nó da grade do motor (n conta nós); a fatia passa pelo nó mais perto da posição pedida' },
  { id: 'esp.terreno-malha', valor: 'o passo da grade', descricao: 'a malha do terreno tem o passo da grade em planta, ancorada no canto sudoeste da caixa das verticais, e chega aos limites da imagem' },
  { id: 'esp.satelite-imagem', valor: 'zoom ≤ 19; lado ≤ 1.024 px', descricao: 'a imagem do terreno é montada das telhas da Esri no maior zoom em que o lado maior não passa de 1.024 px' },
  { id: 'esp.giro-passos', valor: '15° · × 1,25', descricao: 'no 3D, cada seta gira ou inclina 15°, e + e − aproximam e afastam × 1,25' },
  { id: 'esp.sobreposicao-largura', valor: '48 px', descricao: 'a faixa de cada vertical sobre a seção tem 48 px de largura, centrada na estação (encostada na borda do desenho nas pontas)' },
  { id: 'esp.sobreposicao-opacidade', valor: '70 %', descricao: 'as faixas das verticais sobre a seção começam com 70 % de opacidade (de 0 a 100 %); o campo estimado embaixo não muda' },
  { id: 'esp.sobreposicao-escala', valor: 'a mesma em todas', descricao: 'de partida, as faixas de uma grandeza usam a mesma escala (do menor ao maior valor das verticais ligadas, com o zero), para comparar uma vertical com a outra; à escolha, a escala de cada vertical; o Ic vai sempre de 1 a 4, como na trilha dele' },
  { id: 'esp.sobreposicao-trecho', valor: 'meio caminho', descricao: 'na coluna das zonas SBT/SBTn de uma vertical, a zona de cada leitura vale do meio do caminho à leitura de cima ao meio do caminho à de baixo; leitura sem classe (zona 0) deixa o trecho em branco' },
  { id: 'esp.fita-3d', valor: '0,4 × a menor distância entre as verticais (1 a 20 m); no lado escolhido da coluna', descricao: 'no 3D, o gráfico de cada vertical é uma fita em pé na posição dela, a partir da face da coluna no lado escolhido (leste de partida; oeste, norte ou sul), vezes o exagero horizontal dos gráficos: na série, o valor medido pela largura (da face da coluna, no início da escala, até a largura toda, no fim), com o traço do perfil; na zona SBT/SBTn, a fita inteira na cor da zona. A largura toda é 0,4 × a menor distância em planta entre dois lugares distintos, entre 1 e 20 m (5 m com um lugar só)' },
  { id: 'esp.planta-no', valor: 'o nó mais perto', descricao: 'a fatia em planta (no 3D e no 2D sem WebGL) fica na cota do nó da grade mais perto da cota pedida; a zona medida de uma vertical nela é a da leitura mais perto, só dentro do trecho medido (meia célula de folga)' },
].map((e) => Object.freeze({ ...e, evidencia: EVIDENCIA_DA_TELA } as EscolhaDaAnalise)));

/* ── Os ajustes do 3D (onda 347, spec 2026-10-22 §4 e §10) ──────────────── */

/**
 * O que a barra do 3D ajusta, guardado no aparelho pela tela dona (`labmanager.cptu.analise.3d` no LabManager): a forma
 * e o raio das colunas, a cor delas, a opacidade de cada perfil, a malha, a superfície do NA, o exagero horizontal e o
 * lado das fitas, e as camadas geotécnicas (a fonte, o desenho e as unidades escondidas).
 */
export interface AjustesDo3d {
  forma: 'cilindro' | 'prisma';
  /** O raio das colunas, m (`RAIO_DAS_VERTICAIS`); no prisma, a meia largura. Vale para o desenho e para o clique. */
  raio: number;
  corDasVerticais: 'sbtn' | 'sbt' | 'camada' | 'grandeza';
  opacidadeDasVerticais: number;
  malha: boolean;
  superficieDoNa: boolean;
  opacidadeDaAgua: number;
  exageroHorizontal: number;
  ladoDaFita: 'leste' | 'oeste' | 'norte' | 'sul';
  camadas: 'nenhuma' | 'gravadas' | 'tratio';
  desenhoDasCamadas: 'blocos' | 'contatos';
  opacidadeDasCamadas: number;
  unidadesEscondidas: string[];
}

/** O raio das verticais no 3D, m: de 0,2 a 3,0, passo 0,1 (spec §4.1). */
export const RAIO_DAS_VERTICAIS = Object.freeze({ minimo: 0.2, maximo: 3, passo: 0.1 });
/**
 * A opacidade mínima das verticais (spec §4.5): 5 %, para a coluna nunca sumir e continuar clicável — ESCOLHA DA TELA, SEM
 * FONTE [3d.verticais-opacidade-minima].
 */
export const OPACIDADE_MINIMA_DAS_VERTICAIS = 0.05;
/** O exagero horizontal dos gráficos sobre as verticais: de ×1 a ×10, passo 0,5 (spec §4.4). */
export const EXAGERO_HORIZONTAL = Object.freeze({ minimo: 1, maximo: 10, passo: 0.5 });

/**
 * Os ajustes de partida do 3D. O raio de 0,50 m — ESCOLHA DA TELA, SEM FONTE [3d.raio]; a opacidade da água 35 % —
 * ESCOLHA DA TELA, SEM FONTE [3d.na-opacidade]; o exagero horizontal ×1 — ESCOLHA DA TELA, SEM FONTE
 * [3d.exagero-horizontal]; a opacidade das camadas 20 % — ESCOLHA DA TELA, SEM FONTE [3d.camadas-opacidade].
 */
export const AJUSTES_DO_3D_DE_PARTIDA: Readonly<AjustesDo3d> = Object.freeze({
  forma: 'cilindro',
  raio: 0.5, // ESCOLHA DA TELA, SEM FONTE [3d.raio]
  corDasVerticais: 'sbtn',
  opacidadeDasVerticais: 1,
  malha: true,
  superficieDoNa: true,
  opacidadeDaAgua: 0.35, // ESCOLHA DA TELA, SEM FONTE [3d.na-opacidade]
  exageroHorizontal: 1, // ESCOLHA DA TELA, SEM FONTE [3d.exagero-horizontal]
  ladoDaFita: 'leste',
  camadas: 'nenhuma',
  desenhoDasCamadas: 'blocos',
  opacidadeDasCamadas: 0.2, // ESCOLHA DA TELA, SEM FONTE [3d.camadas-opacidade]
  unidadesEscondidas: Object.freeze([]) as unknown as string[],
});

/** As escolhas do 3D sem fonte (spec §10), no molde da `ESCOLHAS_DA_ANALISE`: cada id entre colchetes num comentário daqui. */
export const ESCOLHAS_DO_3D: readonly EscolhaDaAnalise[] = Object.freeze([
  { id: '3d.raio', valor: '0,50 m', descricao: 'o raio de partida das colunas das verticais no 3D é 0,50 m (de 0,2 a 3,0 m, passo 0,1 m)' },
  { id: '3d.na-opacidade', valor: '35 %', descricao: 'a superfície do nível d\'água no 3D começa com 35 % de opacidade (de 0 a 100 %)' },
  { id: '3d.na-minimo', valor: '3 verticais', descricao: 'a superfície do nível d\'água no 3D pede o NA em 3 verticais ou mais (o NA sem número, acima da boca ou abaixo do fundo não conta); com menos, a barra diz por quê' },
  { id: '3d.exagero-horizontal', valor: '× 1', descricao: 'o exagero horizontal dos gráficos sobre as verticais começa em ×1 (de ×1 a ×10, passo 0,5); a prévia mostrou ×3' },
  { id: '3d.verticais-opacidade-minima', valor: '5 %', descricao: 'a opacidade das verticais no 3D vai de 5 % a 100 %: abaixo de 5 % a coluna sumiria e ainda responderia ao clique' },
  { id: '3d.camadas-opacidade', valor: '20 %', descricao: 'as camadas geotécnicas no 3D começam com 20 % de opacidade (de 0 a 100 %)' },
  { id: '3d.camadas-faixas-dos-parametros', valor: 'janela 0,05–20 m; limite 0–1.000; espessura mínima 0–20 m; espaçamento 0,01–20 m', descricao: 'os parâmetros do T-ratio guardados no aparelho (a chave labmanager.cptu.camadas.parametros) só valem dentro destas faixas de sanidade; fora delas, ou com outro tipo, vale o de partida, campo a campo (parametrosDasCamadas.ts)' },
  { id: '3d.camadas-indicador', valor: 'o maior indicador; empate, a mais rasa', descricao: 'cada nó da grade recebe a unidade geotécnica de maior indicador interpolado entre as verticais com camada (o indicador vale 1 na cota que cai numa camada da unidade e 0 fora; pelo IDW das superfícies, ou pela krigagem com o variograma da grandeza do modelo, que vira IDW quando não se ajusta); no empate, a unidade mais rasa; a vertical sem camada naquela cota não vota' },
  { id: '3d.malha-intervalo', valor: '10/20/50/100 m no plano; 1/2/5 m na cota', descricao: 'a malha de cota e coordenadas do 3D tem as linhas a cada 10, 20, 50 ou 100 m no plano e a cada 1, 2 ou 5 m na cota: o menor intervalo que dá de 4 a 8 vãos na maior extensão da grade (em planta) e na altura dela; em graus, os mesmos degraus em 0,0001°, 0,0002°, 0,0005° e 0,001° (≈ 11, 22, 55 e 110 m na latitude)' },
  { id: '3d.na-anel', valor: '1,6 × o raio', descricao: 'o anel que marca o NA de cada vertical na superfície do nível d\'água do 3D tem 1,6 vez o raio da coluna, para aparecer em volta dela (desenho, não conta; Modelo3d.tsx)' },
  { id: 'cam.teto-do-lote', valor: '100 verticais; 200 camadas por vertical', descricao: 'a gravação das camadas leva no máximo 200 camadas por vertical e o lote da janela «Camadas automáticas» no máximo 100 verticais; a tela bloqueia antes de mandar, e o leitor do corpo da rota do lote é dimensionado por eles, a 280 B por camada (5,6 MB: o corpo é lido antes da autenticação, e as rotas irmãs ficam em 4 MB e 1 MB; premissas-validas.ts, web/src/api/cptu.ts, api/src/main.ts)' },
].map((e) => Object.freeze({ ...e, evidencia: EVIDENCIA_DA_TELA } as EscolhaDaAnalise)));

/**
 * Os ajustes do 3D lidos do aparelho (pura): cada campo que serve fica; o que falta, tem outro tipo ou sai da faixa volta ao
 * de partida, campo a campo. O raio vai para o passo de 0,1 m.
 */
export function ajustesDo3dValidos(x: unknown): AjustesDo3d {
  const p = AJUSTES_DO_3D_DE_PARTIDA;
  const o = (x && typeof x === 'object' ? x : {}) as Record<string, unknown>;
  const umDe = <T extends string>(v: unknown, opcoes: readonly T[], padrao: T): T => (opcoes.includes(v as T) ? v as T : padrao);
  const naFaixa = (v: unknown, min: number, max: number, padrao: number): number =>
    (typeof v === 'number' && Number.isFinite(v) && v >= min && v <= max ? v : padrao);
  const logico = (v: unknown, padrao: boolean): boolean => (typeof v === 'boolean' ? v : padrao);
  const raio = naFaixa(o.raio, RAIO_DAS_VERTICAIS.minimo, RAIO_DAS_VERTICAIS.maximo, p.raio);
  const exagero = naFaixa(o.exageroHorizontal, EXAGERO_HORIZONTAL.minimo, EXAGERO_HORIZONTAL.maximo, p.exageroHorizontal);
  return {
    forma: umDe(o.forma, ['cilindro', 'prisma'] as const, p.forma),
    raio: Math.round(raio * 10) / 10,
    corDasVerticais: umDe(o.corDasVerticais, ['sbtn', 'sbt', 'camada', 'grandeza'] as const, p.corDasVerticais),
    opacidadeDasVerticais: naFaixa(o.opacidadeDasVerticais, OPACIDADE_MINIMA_DAS_VERTICAIS, 1, p.opacidadeDasVerticais),
    malha: logico(o.malha, p.malha),
    superficieDoNa: logico(o.superficieDoNa, p.superficieDoNa),
    opacidadeDaAgua: naFaixa(o.opacidadeDaAgua, 0, 1, p.opacidadeDaAgua),
    exageroHorizontal: Math.round(exagero * 2) / 2,
    ladoDaFita: umDe(o.ladoDaFita, ['leste', 'oeste', 'norte', 'sul'] as const, p.ladoDaFita),
    camadas: umDe(o.camadas, ['nenhuma', 'gravadas', 'tratio'] as const, p.camadas),
    desenhoDasCamadas: umDe(o.desenhoDasCamadas, ['blocos', 'contatos'] as const, p.desenhoDasCamadas),
    opacidadeDasCamadas: naFaixa(o.opacidadeDasCamadas, 0, 1, p.opacidadeDasCamadas),
    unidadesEscondidas: Array.isArray(o.unidadesEscondidas) ? o.unidadesEscondidas.filter((u): u is string => typeof u === 'string') : [],
  };
}

/* ── A malha de cota e coordenadas do 3D (onda 347, spec 2026-10-22 §4.2) ─────────── */

/**
 * Os intervalos da malha: no plano, 10, 20, 50 ou 100 m; na cota, 1, 2 ou 5 m; o escolhido dá de 4 a 8 vãos na extensão.
 * ESCOLHA DA TELA, SEM FONTE [3d.malha-intervalo].
 */
export const SERIE_DA_MALHA_NO_PLANO: readonly number[] = Object.freeze([10, 20, 50, 100]); // ESCOLHA DA TELA, SEM FONTE [3d.malha-intervalo]
export const SERIE_DA_MALHA_NA_COTA: readonly number[] = Object.freeze([1, 2, 5]); // ESCOLHA DA TELA, SEM FONTE [3d.malha-intervalo]
export const VAOS_DA_MALHA = Object.freeze({ min: 4, max: 8 });
/** Em graus, o degrau do intervalo do plano (0,0001° ≈ 11 m na latitude) — ESCOLHA DA TELA, SEM FONTE [3d.malha-intervalo]. */
const GRAUS_DO_INTERVALO: Readonly<Record<number, number>> = Object.freeze({ 10: 0.0001, 20: 0.0002, 50: 0.0005, 100: 0.001 });

/** As quatro paredes da caixa da malha, pela normal para fora: norte, sul, leste e oeste. */
export type ParedeDaMalha = 'n' | 's' | 'l' | 'o';
/** O sistema das coordenadas da malha: o das verticais (a regra da 338). */
export type SistemaDaMalha = { tipo: 'graus' } | { tipo: 'utm'; fuso: number };

/** Um rótulo da malha: o texto, o lugar no plano local (x leste, y norte, cota) e as paredes que precisam estar à vista. */
export interface RotuloDaMalha { texto: string; posicao: [number, number, number]; eixo: 'e' | 'n' | 'cota'; paredes: ParedeDaMalha[] }

/** As linhas da malha (pares de pontos, x · cota · −y, no grupo do 3D), os rótulos, os intervalos e a extensão. */
export interface MalhaDoModelo {
  piso: Float32Array;
  paredes: Record<ParedeDaMalha, Float32Array>;
  rotulos: RotuloDaMalha[];
  intervalos: { plano: number; cota: number };
  extensao: { plano: number; cota: number };
}

/**
 * O intervalo da série que dá de `alvo.min` a `alvo.max` vãos na extensão (o menor que dá); sem nenhum, o que fica mais perto
 * da faixa (o menor com pouca extensão, o maior com muita).
 */
export function intervaloDaMalha(extensao: number, alvo: { min: number; max: number }, serie: readonly number[]): number {
  if (!serie.length) return NaN;
  if (!(extensao > 0)) return serie[0];
  let melhor = serie[0], longe = Infinity;
  for (const s of serie) {
    const vaos = extensao / s;
    if (vaos >= alvo.min && vaos <= alvo.max) return s;
    const d = vaos < alvo.min ? alvo.min - vaos : vaos - alvo.max;
    if (d < longe) { longe = d; melhor = s; }
  }
  return melhor;
}

/**
 * O sistema em que as verticais foram lançadas (a regra da onda 338, a do `sistemaDaLocacao` da prancha): UTM quando ao menos
 * uma com coordenada foi lançada em UTM e nenhuma em graus — no fuso mais lançado (o empate fica com o menor) —; graus nos
 * outros casos (as sem registro não contam para nenhum lado).
 */
export function sistemaDaMalha(vs: readonly {
  latitude: number | null; longitude: number | null; lancada?: 'utm' | 'graus' | null; fuso?: number | null;
}[]): SistemaDaMalha {
  const com = vs.filter((v) => v.latitude !== null && v.longitude !== null);
  if (com.some((v) => v.lancada === 'graus')) return { tipo: 'graus' };
  const fusos = new Map<number, number>();
  for (const v of com) if (v.lancada === 'utm' && v.fuso != null) fusos.set(v.fuso, (fusos.get(v.fuso) ?? 0) + 1);
  if (!fusos.size) return { tipo: 'graus' };
  const [fuso] = [...fusos].sort((a, b) => b[1] - a[1] || a[0] - b[0])[0];
  return { tipo: 'utm', fuso };
}

/*
 * Graus (SIRGAS 2000) → E e N no fuso, hemisfério sul: a MESMA conta do `paraUtm` da prancha (api/src/documentos/
 * cptu-graficos.ts, onda 338) — a Transversa de Mercator pelas séries de Krüger até a 4ª ordem, GRS 80, k₀ 0,9996, E falso
 * 500.000 m, N falso 10.000.000 m —, que confere com o banco (`fn_utm_e`/`fn_utm_n`, 0187). Repetida aqui porque o visual
 * roda sozinho no navegador e no Portal (nenhuma biblioteca nova).
 */
const RAD_UTM = Math.PI / 180;
const F_GRS80 = 1 / 298.257222101;
const N_KRUGER = F_GRS80 / (2 - F_GRS80);
const AR_UTM = (6378137 / (1 + N_KRUGER)) * (1 + N_KRUGER ** 2 / 4 + N_KRUGER ** 4 / 64);
const ALFA_UTM = [
  N_KRUGER / 2 - (2 * N_KRUGER ** 2) / 3 + (5 * N_KRUGER ** 3) / 16 + (41 * N_KRUGER ** 4) / 180,
  (13 * N_KRUGER ** 2) / 48 - (3 * N_KRUGER ** 3) / 5 + (557 * N_KRUGER ** 4) / 1440,
  (61 * N_KRUGER ** 3) / 240 - (103 * N_KRUGER ** 4) / 140,
  (49561 * N_KRUGER ** 4) / 161280,
];
const RAIZ_KRUGER = (2 * Math.sqrt(N_KRUGER)) / (1 + N_KRUGER);

/** Graus → E e N (m) no fuso dado, hemisfério sul (a conta da prancha da 338). */
export function grausParaUtm(latitude: number, longitude: number, fuso: number): { e: number; n: number } {
  const fi = latitude * RAD_UTM;
  const dl = (longitude - (-183 + 6 * fuso)) * RAD_UTM;
  const s = Math.sin(fi);
  const t = Math.sinh(Math.atanh(s) - RAIZ_KRUGER * Math.atanh(RAIZ_KRUGER * s));
  const xi = Math.atan2(t, Math.cos(dl));
  const eta = Math.atanh(Math.sin(dl) / Math.sqrt(1 + t * t));
  let x = eta, y = xi;
  for (let j = 1; j <= 4; j++) {
    x += ALFA_UTM[j - 1] * Math.cos(2 * j * xi) * Math.sinh(2 * j * eta);
    y += ALFA_UTM[j - 1] * Math.sin(2 * j * xi) * Math.cosh(2 * j * eta);
  }
  return { e: 500000 + 0.9996 * AR_UTM * x, n: 10000000 + 0.9996 * AR_UTM * y };
}

/**
 * As paredes de fundo no azimute da câmera (graus; 0 = a câmera ao sul, olhando para o norte): as duas de normal (para fora)
 * mais alinhada com a direção em que a câmera olha, (sen a, cos a) em planta — uma de norte/sul e uma de leste/oeste, as mais
 * longe da câmera. No empate (a câmera olhando bem para um eixo), norte e leste.
 */
export function paredesDoFundo(azimute: number): [ParedeDaMalha, ParedeDaMalha] {
  const a = (azimute * Math.PI) / 180;
  const cos = Math.round(Math.cos(a) * 1e9) / 1e9, sen = Math.round(Math.sin(a) * 1e9) / 1e9;
  return [cos >= 0 ? 'n' : 's', sen >= 0 ? 'l' : 'o'];
}

/** «733.440 E», «6.972.900 N»; em graus, «48,6500° O», «27,5000° S»; a cota, «−4 m». */
function textoDaMalha(eixo: 'e' | 'n' | 'cota', v: number, utm: boolean): string {
  if (eixo === 'cota') return `${numeroBr(v, 0)} m`;
  if (utm) return `${numeroBr(v, 0)} ${eixo === 'e' ? 'E' : 'N'}`;
  const lado = eixo === 'e' ? (v < 0 ? 'O' : 'L') : v < 0 ? 'S' : 'N';
  return `${numeroBr(Math.abs(v), 4)}° ${lado}`;
}

/**
 * A malha de cota e coordenadas na caixa da grade (pura): linhas finas no piso (na cota de baixo da grade) e nas quatro paredes
 * (a tela mostra as duas de fundo, `paredesDoFundo`), com os rótulos. E e N no sistema das verticais: em UTM, o E e o N do fuso
 * (a conta da prancha) — a linha de E constante sai levemente inclinada no plano local, pela convergência do meridiano —; em
 * graus, a longitude e a latitude. No piso, as linhas de E e de N; nas paredes norte e sul, as de E em pé e as de cota
 * deitadas; nas de leste e oeste, as de N e as de cota. Os rótulos do E vão no pé da parede norte (ou sul), os do N no pé da
 * de leste (ou oeste), e os da cota na aresta em que as duas de fundo se encontram.
 */
export function linhasDaMalha(g: Pick<RespostaDaGrade, 'grade'>, plano: PlanoLocal, sistema: 'utm' | 'graus', fuso: number | null): MalhaDoModelo {
  const { origem, passo, n } = g.grade;
  const x0 = origem.x, x1 = origem.x + Math.max(0, n.x - 1) * passo.x;
  const y0 = origem.y, y1 = origem.y + Math.max(0, n.y - 1) * passo.y;
  const z0 = origem.cota, z1 = origem.cota + Math.max(0, n.z - 1) * passo.z;
  const extensao = { plano: Math.max(x1 - x0, y1 - y0), cota: z1 - z0 };
  const intervalos = {
    plano: intervaloDaMalha(extensao.plano, VAOS_DA_MALHA, SERIE_DA_MALHA_NO_PLANO),
    cota: intervaloDaMalha(extensao.cota, VAOS_DA_MALHA, SERIE_DA_MALHA_NA_COTA),
  };
  const utm = sistema === 'utm' && fuso !== null && Number.isFinite(fuso);
  // E e N no plano local, afins (a caixa tem centenas de metros: a curvatura não aparece): pelos três pontos da origem.
  const coord = (x: number, y: number): { e: number; n: number } => {
    const geo = plano.paraGeo({ x, y });
    return utm ? grausParaUtm(geo.latitude, geo.longitude, fuso as number) : { e: geo.longitude, n: geo.latitude };
  };
  const c0 = coord(0, 0), cx = coord(100, 0), cy = coord(0, 100);
  const afim = {
    e: { a: (cx.e - c0.e) / 100, b: (cy.e - c0.e) / 100, c: c0.e },
    n: { a: (cx.n - c0.n) / 100, b: (cy.n - c0.n) / 100, c: c0.n },
  };
  const passoDoPlano = utm ? intervalos.plano : GRAUS_DO_INTERVALO[intervalos.plano] ?? intervalos.plano / 1e5;
  const piso: number[] = [];
  const paredes: Record<ParedeDaMalha, number[]> = { n: [], s: [], l: [], o: [] };
  const rotulos: RotuloDaMalha[] = [];
  const seg = (lista: number[], xa: number, ya: number, za: number, xb: number, yb: number, zb: number): void => {
    lista.push(xa, za, -ya, xb, zb, -yb);
  };
  const folga = 1e-6 * Math.max(1, extensao.plano);
  /** Os valores múltiplos do passo entre o menor e o maior (pelo índice inteiro: sem deriva do ponto flutuante). */
  const multiplos = (min: number, max: number, p: number): number[] => {
    const r: number[] = [];
    if (!(p > 0) || !Number.isFinite(min) || !Number.isFinite(max)) return r;
    for (let k = Math.ceil(min / p - 1e-9); k <= Math.floor(max / p + 1e-9) && r.length < 1000; k++) r.push(Number((k * p).toPrecision(12)));
    return r;
  };
  if (x1 > x0 && y1 > y0) {
    for (const eixo of ['e', 'n'] as const) {
      const { a, b, c } = afim[eixo];
      const valor = (x: number, y: number): number => a * x + b * y + c;
      const cantos = [valor(x0, y0), valor(x1, y0), valor(x0, y1), valor(x1, y1)];
      for (const v of multiplos(Math.min(...cantos), Math.max(...cantos), passoDoPlano)) {
        // No piso: a reta a·x + b·y + c = v cortada na caixa (os dois pontos das bordas mais afastados).
        const pts: [number, number][] = [];
        if (Math.abs(b) > 1e-15) for (const x of [x0, x1]) { const y = (v - c - a * x) / b; if (y >= y0 - folga && y <= y1 + folga) pts.push([x, Math.min(y1, Math.max(y0, y))]); }
        if (Math.abs(a) > 1e-15) for (const y of [y0, y1]) { const x = (v - c - b * y) / a; if (x >= x0 - folga && x <= x1 + folga) pts.push([Math.min(x1, Math.max(x0, x)), y]); }
        let par: [[number, number], [number, number]] | null = null, maior = -1;
        for (let i = 0; i < pts.length; i++) for (let j = i + 1; j < pts.length; j++) {
          const d = Math.hypot(pts[i][0] - pts[j][0], pts[i][1] - pts[j][1]);
          if (d > maior) { maior = d; par = [pts[i], pts[j]]; }
        }
        if (par && maior > folga) seg(piso, par[0][0], par[0][1], z0, par[1][0], par[1][1], z0);
        const texto = textoDaMalha(eixo, v, utm);
        if (eixo === 'e') {
          // Nas paredes norte (y1) e sul (y0): a linha em pé onde o E vale v.
          for (const [parede, y] of [['n', y1], ['s', y0]] as const) {
            if (Math.abs(a) < 1e-15) continue;
            const x = (v - c - b * y) / a;
            if (x < x0 - folga || x > x1 + folga) continue;
            const xc = Math.min(x1, Math.max(x0, x));
            seg(paredes[parede], xc, y, z0, xc, y, z1);
            rotulos.push({ texto, posicao: [xc, y, z0], eixo, paredes: [parede] });
          }
        } else {
          for (const [parede, x] of [['l', x1], ['o', x0]] as const) {
            if (Math.abs(b) < 1e-15) continue;
            const y = (v - c - a * x) / b;
            if (y < y0 - folga || y > y1 + folga) continue;
            const yc = Math.min(y1, Math.max(y0, y));
            seg(paredes[parede], x, yc, z0, x, yc, z1);
            rotulos.push({ texto, posicao: [x, yc, z0], eixo, paredes: [parede] });
          }
        }
      }
    }
  }
  // A cota: deitada nas quatro paredes; o rótulo na aresta das duas de fundo.
  for (const v of multiplos(z0, z1, intervalos.cota)) {
    seg(paredes.n, x0, y1, v, x1, y1, v);
    seg(paredes.s, x0, y0, v, x1, y0, v);
    seg(paredes.l, x1, y0, v, x1, y1, v);
    seg(paredes.o, x0, y0, v, x0, y1, v);
    for (const ns of ['n', 's'] as const) {
      for (const lo of ['l', 'o'] as const) {
        rotulos.push({ texto: textoDaMalha('cota', v, utm), posicao: [lo === 'l' ? x1 : x0, ns === 'n' ? y1 : y0, v], eixo: 'cota', paredes: [ns, lo] });
      }
    }
  }
  // A moldura de cada parede e do piso.
  const moldura = (lista: number[], cantos: [number, number, number][]): void => {
    for (let i = 0; i < cantos.length; i++) {
      const a = cantos[i], b = cantos[(i + 1) % cantos.length];
      seg(lista, a[0], a[1], a[2], b[0], b[1], b[2]);
    }
  };
  moldura(piso, [[x0, y0, z0], [x1, y0, z0], [x1, y1, z0], [x0, y1, z0]]);
  moldura(paredes.n, [[x0, y1, z0], [x1, y1, z0], [x1, y1, z1], [x0, y1, z1]]);
  moldura(paredes.s, [[x0, y0, z0], [x1, y0, z0], [x1, y0, z1], [x0, y0, z1]]);
  moldura(paredes.l, [[x1, y0, z0], [x1, y1, z0], [x1, y1, z1], [x1, y0, z1]]);
  moldura(paredes.o, [[x0, y0, z0], [x0, y1, z0], [x0, y1, z1], [x0, y0, z1]]);
  return {
    piso: Float32Array.from(piso),
    paredes: { n: Float32Array.from(paredes.n), s: Float32Array.from(paredes.s), l: Float32Array.from(paredes.l), o: Float32Array.from(paredes.o) },
    rotulos, intervalos, extensao,
  };
}

/* ── As grandezas da seção ─────────────────────────────────────────────── */

export type GrandezaDaSecao = 'sbtn' | 'ic' | 'qc' | 'qt' | 'fs' | 'u2' | 'su';

/** Uma coluna interpolada: log₁₀ Fr, log₁₀ Qtn ou uma grandeza do motor. */
export type ColunaDaSecao = 'logFr' | 'logQtn' | Grandeza;

/** As sete grandezas da seção (spec §5.7; R5): o rótulo, a unidade, a escala e as colunas que cada uma interpola. */
export const GRANDEZAS_DA_SECAO: readonly {
  chave: GrandezaDaSecao; rotulo: string; unidade: string; escala: EscalaDoEixo; colunas: readonly ColunaDaSecao[];
}[] = Object.freeze([
  { chave: 'sbtn', rotulo: 'SBTn', unidade: '', escala: 'linear', colunas: ['logFr', 'logQtn', 'ic'] },
  { chave: 'ic', rotulo: 'Ic', unidade: '', escala: 'linear', colunas: ['ic'] },
  { chave: 'qc', rotulo: 'qc', unidade: 'MPa', escala: 'log', colunas: ['qc'] },
  { chave: 'qt', rotulo: 'qt', unidade: 'MPa', escala: 'log', colunas: ['qt'] },
  { chave: 'fs', rotulo: 'fs', unidade: 'kPa', escala: 'log', colunas: ['fs'] },
  { chave: 'u2', rotulo: 'u₂', unidade: 'kPa', escala: 'linear', colunas: ['u2'] },
  { chave: 'su', rotulo: 'Su', unidade: 'kPa', escala: 'linear', colunas: ['su', 'logFr', 'logQtn'] },
].map((g) => Object.freeze({ ...g, colunas: Object.freeze(g.colunas) }) as { chave: GrandezaDaSecao; rotulo: string; unidade: string; escala: EscalaDoEixo; colunas: readonly ColunaDaSecao[] }));

/** A definição de uma grandeza da seção. */
export function grandezaDaSecao(chave: GrandezaDaSecao): (typeof GRANDEZAS_DA_SECAO)[number] {
  return GRANDEZAS_DA_SECAO.find((g) => g.chave === chave) ?? GRANDEZAS_DA_SECAO[1];
}

/** A coluna PRINCIPAL de uma grandeza: a que a seção desenha e cujo COV vai na faixa (no SBTn, o Ic). */
export function colunaPrincipal(chave: GrandezaDaSecao): ColunaDaSecao {
  return chave === 'sbtn' ? 'ic' : chave;
}

/** A coluna do motor de que uma coluna da seção sai (para a marca de homologação). */
const colunaDoMotor = (c: ColunaDaSecao): Grandeza => (c === 'logFr' ? 'fr' : c === 'logQtn' ? 'qtn' : c);

/* ── Quem entra ────────────────────────────────────────────────────────── */

/** Uma vertical da campanha como a análise a vê: o cabeçalho e o `Resultado` gravado. */
export interface VerticalDaAnalise {
  codigo: string;
  latitude: number | null;
  longitude: number | null;
  cota: number | null;
  r: Resultado;
}

const temNumero = (v: number | null | undefined): v is number => v !== null && v !== undefined && Number.isFinite(v);

/** A vertical tem latitude, longitude e cota. */
const util = (v: VerticalDaAnalise): boolean => temNumero(v.latitude) && temNumero(v.longitude) && temNumero(v.cota);

/** Os grupos de verticais no mesmo lugar (a até `LUGAR_IGUAL` em planta, como o motor junta), com 2 ou mais. */
function gruposNoMesmoLugar(vs: readonly { codigo: string; latitude: number; longitude: number }[]): string[][] {
  const plano = planoLocal(vs);
  const pontos = vs.map((v) => plano.paraPlano(v.latitude, v.longitude));
  const grupos: { x: number; y: number; codigos: string[] }[] = [];
  vs.forEach((v, i) => {
    const g = grupos.find((x) => Math.hypot(x.x - pontos[i].x, x.y - pontos[i].y) <= LUGAR_IGUAL);
    if (g) g.codigos.push(v.codigo);
    else grupos.push({ x: pontos[i].x, y: pontos[i].y, codigos: [v.codigo] });
  });
  return grupos.filter((g) => g.codigos.length > 1).map((g) => g.codigos);
}

/**
 * Quem entra no espacial (R4): as verticais com latitude, longitude e cota; as outras vêm listadas com o
 * motivo («sem coordenada» primeiro); e os grupos no mesmo lugar (a até 1 cm, como o motor junta), que entram
 * pela média — a tela diz.
 */
export function separarVerticais(vs: readonly VerticalDaAnalise[]): {
  uteis: VerticalDaAnalise[];
  fora: { codigo: string; motivo: 'sem coordenada' | 'sem cota' }[];
  noMesmoLugar: string[][];
} {
  const uteis = vs.filter(util);
  const fora = vs.filter((v) => !util(v)).map((v) => ({
    codigo: v.codigo,
    motivo: (temNumero(v.latitude) && temNumero(v.longitude) ? 'sem cota' : 'sem coordenada') as 'sem coordenada' | 'sem cota',
  }));
  const noMesmoLugar = gruposNoMesmoLugar(uteis.map((v) => ({ codigo: v.codigo, latitude: v.latitude as number, longitude: v.longitude as number })));
  return { uteis, fora, noMesmoLugar };
}

/**
 * As grandezas que a tela oferece, com a marca de homologação (`combinarMarcas` das colunas de cada uma em
 * cada vertical, com as premissas dela). Com `mostrarNaoHomologadas` falso (o Portal), só as homologadas.
 */
export function grandezasDaSecao(vs: readonly VerticalDaAnalise[], x: { mostrarNaoHomologadas: boolean }):
{ chave: GrandezaDaSecao; marca: MarcaDeHomologacao }[] {
  const saida: { chave: GrandezaDaSecao; marca: MarcaDeHomologacao }[] = [];
  // Sem vertical não há o que marcar: nenhuma grandeza se oferece (nunca «homologada» por falta de quem diga — M10 da
  // revisão da 316.2).
  if (!vs.length) return saida;
  for (const g of GRANDEZAS_DA_SECAO) {
    const partes: { marca: MarcaDeHomologacao; rotulo?: string }[] = [];
    for (const v of vs) {
      for (const c of g.colunas) partes.push({ marca: marcaDaColuna(colunaDoMotor(c), v.r.premissas) });
      // A zona do SBTn e do Su é a do classificador: a coluna `sbtn` entra na marca (o R3 fala em «SBTn e Ic homologados»).
      if (g.chave === 'sbtn' || g.chave === 'su') partes.push({ marca: marcaDaColuna('sbtn', v.r.premissas) });
    }
    const marca = combinarMarcas(partes);
    if (!marca.mostrar) continue;
    if (!x.mostrarNaoHomologadas && !marca.homologada) continue;
    saida.push({ chave: g.chave, marca });
  }
  return saida;
}

/* ── A linha de corte ──────────────────────────────────────────────────── */

/** Uma linha de corte gravada na campanha (`cptu_campanha.linhas_corte`, R3): o PDF desenha com o método e o modelo dela. */
export interface LinhaDeCorte {
  /** «A–B», «C–D»… com a meia-risca (U+2013). */
  nome: string;
  pontos: readonly { latitude: number; longitude: number }[];
  metodo: 'idw' | 'krigagem';
  modelo: ModeloDeVariograma;
}

/** A meia-risca dos nomes das linhas. */
const MEIA_RISCA = '–';
const LETRAS = 'ABCDEFGHIJKL';

/** O próximo par de letras livre («A–B», «C–D»… «K–L»); null com seis linhas. */
export function proximoNome(linhas: readonly LinhaDeCorte[]): string | null {
  const usados = new Set(linhas.map((l) => l.nome));
  for (let i = 0; i + 1 < LETRAS.length; i += 2) {
    const nome = `${LETRAS[i]}${MEIA_RISCA}${LETRAS[i + 1]}`;
    if (!usados.has(nome)) return nome;
  }
  return null;
}

/* ── O pedido ──────────────────────────────────────────────────────────── */

/** Uma vertical no pedido da seção: só os vetores de que a conta precisa (vai ao worker por cópia estruturada). */
export interface VerticalNoPedido {
  codigo: string;
  latitude: number;
  longitude: number;
  cota: number;
  z: Float64Array;
  colunas: Partial<Record<ColunaDaSecao, Float64Array>>;
  /** `r.sbtn`: a zona MEDIDA, para a faixa da estação. */
  zonaMedida: Int8Array;
  /** `r.sbt`: a zona SBT medida, para colorir as colunas do 3D pelo SBT (onda 347); sem ela, a coluna fica cinza. */
  zonaSbt?: Int8Array;
  /**
   * O NA da vertical (m abaixo da boca, o das premissas dela), para a superfície do nível d'água do 3D; null sem NA (onda 347).
   * `undefined` em todas = o pedido não sabe do NA (o pacote do Portal emitido sem o campo): o 3D esconde a água.
   */
  na?: number | null;
}

export interface PedidoDaSecao {
  verticais: readonly VerticalNoPedido[];
  linha: LinhaDeCorte['pontos'];
  grandeza: GrandezaDaSecao;
  metodo: 'idw' | 'krigagem';
  modelo: ModeloDeVariograma;
  passoHorizontal: number;
  passoVertical: number;
}

/** log₁₀ de uma série: NaN onde o valor não é > 0 (o fs = 0 no topo, o Fr negativo). */
const log10DaSerie = (s: ArrayLike<number>): Float64Array => Float64Array.from(s, (x) => (x > 0 ? Math.log10(x) : NaN));

/** Uma coluna da seção a partir do `Resultado` (log₁₀ Fr e log₁₀ Qtn são da tela — ESCOLHA DA TELA, SEM FONTE [esp.log-fr-qtn]). */
export function colunaDoResultado(r: Resultado, c: ColunaDaSecao): Float64Array {
  if (c === 'logFr') return log10DaSerie(r.col.fr);
  if (c === 'logQtn') return log10DaSerie(r.col.qtn);
  const s = r.col[c];
  return s ? Float64Array.from(s) : new Float64Array(r.n).fill(NaN);
}

/** As verticais úteis como vão num pedido (da seção ou da grade): só as colunas da grandeza. */
function verticaisNoPedido(vs: readonly VerticalDaAnalise[], grandeza: GrandezaDaSecao): VerticalNoPedido[] {
  const { uteis } = separarVerticais(vs);
  const def = grandezaDaSecao(grandeza);
  return uteis.map((v) => {
    const colunas: Partial<Record<ColunaDaSecao, Float64Array>> = {};
    for (const c of def.colunas) colunas[c] = colunaDoResultado(v.r, c);
    return {
      codigo: v.codigo, latitude: v.latitude as number, longitude: v.longitude as number, cota: v.cota as number,
      z: Float64Array.from(v.r.col.z), colunas, zonaMedida: Int8Array.from(v.r.sbtn),
      ...(v.r.sbt ? { zonaSbt: Int8Array.from(v.r.sbt) } : {}),
    };
  });
}

/** O pedido da seção: as verticais úteis, só com as colunas da grandeza. */
export function pedidoDaSecao(vs: readonly VerticalDaAnalise[], x: Omit<PedidoDaSecao, 'verticais'>): PedidoDaSecao {
  const verticais = verticaisNoPedido(vs, x.grandeza);
  return {
    verticais, linha: x.linha.map((p) => ({ latitude: p.latitude, longitude: p.longitude })), grandeza: x.grandeza,
    metodo: x.metodo, modelo: x.modelo, passoHorizontal: x.passoHorizontal, passoVertical: x.passoVertical,
  };
}

/* ── A resposta ────────────────────────────────────────────────────────── */

/** O variograma de uma coluna interpolada: o experimental (das cotas), o ajustado e os parâmetros da conta. */
export interface VariogramaDaColuna {
  coluna: string;
  experimental: ParExperimental[];
  ajustado: Variograma & { ajustado: boolean; motivo: string | null };
  parametros: { classes: number; hMax: number; passoDasCotas: number };
}

export interface EstacaoDaSecao {
  codigo: string;
  distancia: number;
  afastamento: number;
  /** A coluna mais próxima. */
  coluna: number;
  /** A zona MEDIDA (`r.sbtn`) da leitura mais próxima de cada cota da seção; 0 fora da vertical. */
  medida: Int8Array;
}

export interface RespostaDaSecao {
  /** A seção da coluna principal (no SBTn, o Ic): as distâncias, as cotas e os valores dela. */
  secao: Secao;
  /** Os valores de cada coluna interpolada, linha a linha (cotas × distâncias). */
  valores: Record<string, Float64Array>;
  /** A zona SBTn estimada por célula (SBTn e Su); 0 fora do pintado; null nas outras grandezas. */
  zonas: Int8Array | null;
  /** O recorte pelo terreno, por coluna: a linha das bocas e a dos fundos; `de`/`ate` = a primeira e a última estação. */
  recorte: { boca: Float64Array; fundo: Float64Array; de: number; ate: number };
  /** 1 = dentro do terreno, entre as estações, com valor (e, no Su, zona argilosa). */
  pintar: Uint8Array;
  estacoes: EstacaoDaSecao[];
  variogramas: VariogramaDaColuna[];
  metodoUsado: 'idw' | 'krigagem';
  motivoDoMetodo: string | null;
  /** Por que a seção não se desenha («só 1 vertical a até 30 m da linha»); null quando se desenha. */
  insuficiente: string | null;
  noMesmoLugar: string[][];
  /** O COV da coluna principal, por cota. */
  cov: Float64Array;
}

/** A vertical do pedido como o motor a lê, numa coluna. */
function verticalEspacial(v: VerticalNoPedido, c: ColunaDaSecao): VerticalEspacial {
  return { codigo: v.codigo, latitude: v.latitude, longitude: v.longitude, cota: v.cota, profundidade: v.z, valor: v.colunas[c] ?? new Float64Array(0) };
}

/** A metade da maior distância em planta entre os lugares distintos — o hMax da tela, o mesmo que o motor usa. */
function hMaxDe(vs: readonly VerticalNoPedido[]): number {
  const plano = planoLocal(vs);
  const pontos = vs.map((v) => plano.paraPlano(v.latitude, v.longitude));
  let maior = 0;
  for (let i = 0; i < pontos.length; i++) {
    for (let j = i + 1; j < pontos.length; j++) maior = Math.max(maior, Math.hypot(pontos[i].x - pontos[j].x, pontos[i].y - pontos[j].y));
  }
  return maior / 2;
}

/** O variograma de uma coluna: o experimental das cotas e o ajuste pelo modelo pedido. */
function variogramaDaColuna(vs: readonly VerticalNoPedido[], c: ColunaDaSecao, modelo: ModeloDeVariograma): VariogramaDaColuna {
  const hMax = hMaxDe(vs);
  const experimental = variogramaDasCotas(vs.map((v) => verticalEspacial(v, c)), {
    passoVertical: PASSO_DAS_COTAS_DO_VARIOGRAMA, classes: CLASSES_DO_VARIOGRAMA,
  });
  return {
    coluna: c, experimental, ajustado: ajustarVariograma(experimental, modelo),
    parametros: { classes: CLASSES_DO_VARIOGRAMA, hMax, passoDasCotas: PASSO_DAS_COTAS_DO_VARIOGRAMA },
  };
}

/** A frase de «quantas verticais» a até 30 m. */
const fraseDasVerticais = (n: number): string => `${n === 0 ? 'nenhuma' : `só ${n}`} vertical a até ${AFASTAMENTO_DA_LINHA} m da linha`;

/** «CPTu09 e CPTu10», «CPTu01, CPTu02 e CPTu03». */
const listaComE = (xs: readonly string[]): string => (xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} e ${xs[xs.length - 1]}`);

/**
 * Por que a seção não se desenha quando a linha pega menos de dois lugares: «só 1 vertical a até 30 m da linha», ou,
 * com duas ou mais verticais num lugar só, quem está no mesmo lugar (I1 da revisão da 316.2) — nunca a seção vazia
 * calada.
 */
function fraseDosLugares(lugares: number, estacoes: readonly { codigo: string }[], noMesmoLugar: readonly string[][]): string {
  if (estacoes.length < 2) return fraseDasVerticais(estacoes.length);
  const codigos = estacoes.map((e) => e.codigo);
  const juntas = noMesmoLugar.some((g) => codigos.every((c) => g.includes(c)));
  return `só ${lugares} lugar a até ${AFASTAMENTO_DA_LINHA} m da linha: ${listaComE(codigos)} ${juntas ? 'no mesmo lugar' : 'na mesma distância ao longo dela'}`;
}

/** O passo da seção que não é um número maior que zero (M3 da revisão da 316.2). */
export const FRASE_DO_PASSO_DA_SECAO = 'o passo da seção não serve: escolha um passo maior que zero';
/** A linha com um vértice sem coordenada (M3 da revisão da 316.2). */
export const FRASE_DA_LINHA_SEM_PONTO = 'a linha tem um ponto sem coordenada: trace a linha de novo';
/** A seção acima de `LIMITE_DE_CELULAS_DA_SECAO` (o motor a devolve vazia). */
export const FRASE_DO_LIMITE_DA_SECAO = 'a seção passa do limite: aumente o passo';

/** A resposta vazia, com o porquê. */
function secaoVazia(insuficiente: string, noMesmoLugar: string[][], metodo: 'idw' | 'krigagem'): RespostaDaSecao {
  return {
    secao: { distancias: new Float64Array(0), cotas: new Float64Array(0), valores: new Float64Array(0), verticais: [], cov: new Float64Array(0) },
    valores: {}, zonas: null, recorte: { boca: new Float64Array(0), fundo: new Float64Array(0), de: NaN, ate: NaN },
    pintar: new Uint8Array(0), estacoes: [], variogramas: [], metodoUsado: metodo, motivoDoMetodo: null, insuficiente, noMesmoLugar,
    cov: new Float64Array(0),
  };
}

/** O índice da coluna mais próxima de uma distância. */
function colunaMaisProxima(distancias: ArrayLike<number>, d: number): number {
  let melhor = 0;
  for (let i = 1; i < distancias.length; i++) if (Math.abs(distancias[i] - d) < Math.abs(distancias[melhor] - d)) melhor = i;
  return melhor;
}

/** A célula (coluna, linha) da seção mais próxima de uma distância e de uma cota; null na seção vazia. */
export function celulaMaisProxima(s: Secao, distancia: number, cota: number): { coluna: number; linha: number } | null {
  if (!s.distancias.length || !s.cotas.length) return null;
  return { coluna: colunaMaisProxima(s.distancias, distancia), linha: colunaMaisProxima(s.cotas, cota) };
}

/**
 * A seção de uma grandeza ao longo da linha (pura; o worker a chama): o variograma de cada coluna, o método
 * (a krigagem só com TODAS as colunas ajustadas; senão o IDW com a frase), a seção do motor por coluna, as
 * zonas estimadas, o recorte pelo terreno e a faixa medida de cada estação.
 */
export function calcularSecao(p: PedidoDaSecao): RespostaDaSecao {
  const def = grandezaDaSecao(p.grandeza);
  const principal = colunaPrincipal(p.grandeza);
  const vs = p.verticais.filter((v) => Number.isFinite(v.latitude) && Number.isFinite(v.longitude) && Number.isFinite(v.cota));
  const noMesmoLugar = gruposNoMesmoLugar(vs);

  // O que não é «passa do limite» sai antes de qualquer conta, com a frase dele (M3 da revisão da 316.2): o passo que
  // não é um número maior que zero e a linha com um vértice sem coordenada.
  const passoServe = [p.passoHorizontal, p.passoVertical].every((v) => Number.isFinite(v) && v > 0);
  const linhaServe = p.linha.length >= 2 && p.linha.every((q) => Number.isFinite(q.latitude) && Number.isFinite(q.longitude));
  if (!passoServe || !linhaServe) return secaoVazia(!passoServe ? FRASE_DO_PASSO_DA_SECAO : FRASE_DA_LINHA_SEM_PONTO, noMesmoLugar, p.metodo);
  const variogramas = def.colunas.map((c) => variogramaDaColuna(vs, c, p.modelo));

  let metodoUsado: 'idw' | 'krigagem' = p.metodo;
  let motivoDoMetodo: string | null = null;
  if (p.metodo === 'krigagem') {
    const falha = variogramas.find((v) => !v.ajustado.ajustado);
    if (falha) {
      metodoUsado = 'idw';
      motivoDoMetodo = `krigagem indisponível: ${falha.ajustado.motivo ?? 'o variograma não se ajusta'} — a seção usa o IDW (potência ${POTENCIA_DO_IDW})`;
    }
  }

  const valores: Record<string, Float64Array> = {};
  let secao: Secao | null = null;
  def.colunas.forEach((c, i) => {
    const s = secaoAoLongoDaLinha(vs.map((v) => verticalEspacial(v, c)), p.linha, {
      metodo: metodoUsado === 'idw' ? { tipo: 'idw', potencia: POTENCIA_DO_IDW } : { tipo: 'krigagem', variograma: variogramas[i].ajustado },
      passoHorizontal: p.passoHorizontal, passoVertical: p.passoVertical, afastamentoMaximo: AFASTAMENTO_DA_LINHA,
    });
    valores[c] = s.valores;
    if (c === principal) secao = s;
  });
  const sec = secao as unknown as Secao;
  const nd = sec.distancias.length;
  const nc = sec.cotas.length;
  const n = nd * nc;

  // As estações: a coluna mais próxima e a faixa medida.
  const porCodigo = new Map(vs.map((v) => [v.codigo, v]));
  const estacoes: EstacaoDaSecao[] = sec.verticais.map((e) => {
    const v = porCodigo.get(e.codigo);
    const medida = new Int8Array(nc);
    if (v) {
      const fundo = v.cota - (v.z.length ? v.z[v.z.length - 1] : 0);
      for (let l = 0; l < nc; l++) {
        const cota = sec.cotas[l];
        if (!(cota <= v.cota + FOLGA && cota >= fundo - FOLGA)) continue;
        const i = indiceNaProfundidade(v.z, v.cota - cota);
        medida[l] = i >= 0 ? v.zonaMedida[i] ?? 0 : 0;
      }
    }
    return { codigo: e.codigo, distancia: e.distancia, afastamento: e.afastamento, coluna: nd ? colunaMaisProxima(sec.distancias, e.distancia) : 0, medida };
  });

  // O recorte: a linha das bocas e a dos fundos, pelas estações (as no mesmo lugar pela média), interpoladas
  // linearmente; nada antes da primeira nem depois da última — ESCOLHA DA TELA, SEM FONTE [esp.recorte-estacoes].
  const marcos: { d: number; boca: number; fundo: number; n: number }[] = [];
  for (const e of estacoes) {
    const v = porCodigo.get(e.codigo);
    if (!v) continue;
    const fundo = v.cota - (v.z.length ? v.z[v.z.length - 1] : 0);
    const m = marcos.find((x) => Math.abs(x.d - e.distancia) <= LUGAR_IGUAL);
    if (m) { m.boca += v.cota; m.fundo += fundo; m.n++; } else marcos.push({ d: e.distancia, boca: v.cota, fundo, n: 1 });
  }
  for (const m of marcos) { m.boca /= m.n; m.fundo /= m.n; }
  marcos.sort((a, b) => a.d - b.d);
  const boca = new Float64Array(nd).fill(NaN);
  const fundo = new Float64Array(nd).fill(NaN);
  const de = marcos.length ? marcos[0].d : NaN;
  const ate = marcos.length ? marcos[marcos.length - 1].d : NaN;
  for (let i = 0; i < nd; i++) {
    const d = sec.distancias[i];
    if (!(d >= de - LUGAR_IGUAL && d <= ate + LUGAR_IGUAL)) continue;
    // A coluna da estação (a até 1 cm dela) leva a boca e o fundo da própria estação.
    const proprio = marcos.find((x) => Math.abs(x.d - d) <= LUGAR_IGUAL);
    if (proprio) { boca[i] = proprio.boca; fundo[i] = proprio.fundo; continue; }
    let k = 0;
    while (k + 1 < marcos.length && marcos[k + 1].d < d - FOLGA) k++;
    const a = marcos[k];
    const b = marcos[Math.min(k + 1, marcos.length - 1)];
    const t = b.d - a.d > FOLGA ? Math.min(Math.max((d - a.d) / (b.d - a.d), 0), 1) : 0;
    boca[i] = a.boca + t * (b.boca - a.boca);
    fundo[i] = a.fundo + t * (b.fundo - a.fundo);
  }

  // A seção precisa de DOIS LUGARES a até 30 m da linha, não de duas estações (I1 da revisão da 316.2): duas verticais
  // no mesmo lugar (o CPTu09 e o CPTu10 do 2609350) dão uma estação só no recorte, e a tela diz quem e por quê.
  const insuficiente = marcos.length < 2
    ? fraseDosLugares(marcos.length, estacoes, noMesmoLugar)
    : n === 0 ? FRASE_DO_LIMITE_DA_SECAO : null;

  // As zonas estimadas e o que se pinta.
  const comZona = p.grandeza === 'sbtn' || p.grandeza === 'su';
  const zonas = comZona ? new Int8Array(n) : null;
  const pintar = new Uint8Array(n);
  const principalValores = valores[principal];
  if (!insuficiente) {
    const logFr = valores.logFr;
    const logQtn = valores.logQtn;
    for (let l = 0; l < nc; l++) {
      const cota = sec.cotas[l];
      for (let i = 0; i < nd; i++) {
        const k = l * nd + i;
        const dentro = Number.isFinite(boca[i]) && cota <= boca[i] + FOLGA && cota >= fundo[i] - FOLGA;
        let z = 0;
        if (zonas && logFr && logQtn && Number.isFinite(logFr[k]) && Number.isFinite(logQtn[k])) z = zonaSbtn(10 ** logFr[k], 10 ** logQtn[k]);
        const comValor = p.grandeza === 'sbtn' ? z > 0 : Number.isFinite(principalValores[k]) && (p.grandeza !== 'su' || zonaArgilosa(z));
        const pinta = dentro && comValor;
        pintar[k] = pinta ? 1 : 0;
        if (zonas) zonas[k] = pinta ? z : 0;
      }
    }
  }

  return {
    secao: sec, valores, zonas, recorte: { boca, fundo, de, ate }, pintar, estacoes, variogramas, metodoUsado, motivoDoMetodo,
    insuficiente, noMesmoLugar, cov: sec.cov,
  };
}

/* ── A grade do 3D: o fecho, as superfícies e o recorte (Tarefa 316.3) ── */

/** A frase do R4 quando o 3D não tem três verticais fora de uma linha. */
export const FRASE_DAS_ALINHADAS = 'O modelo 3D precisa de três verticais com coordenada e cota que não estejam alinhadas.';
/** O passo da grade que não é um número maior que zero (0, negativo, NaN) — não é «passa do limite». */
export const FRASE_DO_PASSO = 'o passo da grade não serve: escolha um passo maior que zero';
/** A grade acima de `LIMITE_DE_VOXELS` (o motor a devolve vazia). */
export const FRASE_DO_LIMITE_DA_GRADE = 'a grade passa do limite: aumente o passo';

/** O pedido da grade do 3D: as verticais (como no pedido da seção), a grandeza, o método e o passo. */
export interface PedidoDaGrade {
  verticais: readonly VerticalNoPedido[];
  grandeza: GrandezaDaSecao;
  metodo: 'idw' | 'krigagem';
  modelo: ModeloDeVariograma;
  passo: { x: number; y: number; z: number };
}

/** Uma vertical da grade no plano local das verticais úteis: a boca e o fundo, em m. */
export interface VerticalNaGrade { codigo: string; x: number; y: number; boca: number; fundo: number }

export interface RespostaDaGrade {
  /** A grandeza do pedido (a fatia e os voxels escolhem a cor por ela). */
  grandeza: GrandezaDaSecao;
  /** A grade da coluna principal (no SBTn, o Ic); índice = ix + nx·(iy + ny·iz), iz crescendo para cima. */
  grade: GradeDeVoxels;
  /** Os valores de cada coluna interpolada, no índice da grade. */
  valores: Record<string, Float32Array>;
  /** A zona SBTn estimada por nó (SBTn e Su); 0 fora do visível; null nas outras grandezas. */
  zonas: Int8Array | null;
  /** 1 = dentro do fecho convexo e entre as superfícies (IDW das bocas e dos fundos), com valor (e, no Su, zona argilosa). */
  visivel: Uint8Array;
  /** A superfície das bocas e a dos fundos em cada nó em planta (nx × ny, índice ix + nx·iy), m. */
  boca: Float32Array;
  fundo: Float32Array;
  /** O fecho convexo das verticais úteis, no plano local delas, anti-horário. */
  fecho: PontoPlano[];
  /** As verticais úteis no plano local (as do mesmo lugar, cada uma). */
  verticais: VerticalNaGrade[];
  variogramas: VariogramaDaColuna[];
  metodoUsado: 'idw' | 'krigagem';
  motivoDoMetodo: string | null;
  /** Por que o 3D não se desenha (a frase do R4 com três alinhadas; a grade que passa do limite); null quando se desenha. */
  insuficiente: string | null;
  noMesmoLugar: string[][];
}

/** O pedido da grade: as verticais úteis, só com as colunas da grandeza (o mesmo vetor do pedido da seção). */
export function pedidoDaGrade(vs: readonly VerticalDaAnalise[], x: Omit<PedidoDaGrade, 'verticais'>): PedidoDaGrade {
  return { verticais: verticaisNoPedido(vs, x.grandeza), grandeza: x.grandeza, metodo: x.metodo, modelo: x.modelo, passo: { ...x.passo } };
}

/** O produto vetorial (a − o) × (b − o). */
const vetorial = (o: PontoPlano, a: PontoPlano, b: PontoPlano): number => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);

/**
 * O fecho convexo (cadeia monótona de Andrew), anti-horário, começando no ponto de menor x (e menor y). Os
 * pontos no mesmo lugar contam uma vez, e o ponto a até `LUGAR_IGUAL` (1 cm, o mesmo lugar do motor) da reta
 * de dois vértices não é vértice — três verticais nessa folga de uma linha estão alinhadas, e o fecho delas é
 * o segmento (2 pontos).
 */
export function fechoConvexo(p: readonly PontoPlano[]): PontoPlano[] {
  const unicos: PontoPlano[] = [];
  for (const q of p) {
    if (!Number.isFinite(q.x) || !Number.isFinite(q.y)) continue;
    if (!unicos.some((u) => Math.hypot(u.x - q.x, u.y - q.y) <= LUGAR_IGUAL)) unicos.push({ x: q.x, y: q.y });
  }
  unicos.sort((a, b) => a.x - b.x || a.y - b.y);
  if (unicos.length < 3) return unicos;
  // Vira à esquerda «de verdade» só quando b está a mais de 1 cm da reta o→a.
  const esquerda = (o: PontoPlano, a: PontoPlano, b: PontoPlano): boolean => {
    const l = Math.hypot(b.x - o.x, b.y - o.y);
    return l > 0 && vetorial(o, a, b) / l > LUGAR_IGUAL;
  };
  const meia = (lista: PontoPlano[]): PontoPlano[] => {
    const h: PontoPlano[] = [];
    for (const q of lista) {
      while (h.length >= 2 && !esquerda(h[h.length - 2], h[h.length - 1], q)) h.pop();
      h.push(q);
    }
    return h;
  };
  const baixo = meia(unicos);
  const cima = meia([...unicos].reverse());
  return [...baixo.slice(0, -1), ...cima.slice(0, -1)];
}

/** A distância de p ao segmento ab. */
function distanciaAoSegmento(p: PontoPlano, a: PontoPlano, b: PontoPlano): number {
  const dx = b.x - a.x, dy = b.y - a.y;
  const l2 = dx * dx + dy * dy;
  const t = l2 > 0 ? Math.min(Math.max(((p.x - a.x) * dx + (p.y - a.y) * dy) / l2, 0), 1) : 0;
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

/**
 * O ponto está dentro do fecho (anti-horário)? A borda conta, com a folga do mesmo lugar do motor (1 cm). O
 * fecho de um ponto ou de um segmento (verticais alinhadas) só contém o que está a até 1 cm dele.
 */
export function dentroDoFecho(p: PontoPlano, fecho: readonly PontoPlano[]): boolean {
  if (!Number.isFinite(p.x) || !Number.isFinite(p.y) || !fecho.length) return false;
  if (fecho.length === 1) return Math.hypot(p.x - fecho[0].x, p.y - fecho[0].y) <= LUGAR_IGUAL;
  if (fecho.length === 2) return distanciaAoSegmento(p, fecho[0], fecho[1]) <= LUGAR_IGUAL;
  for (let i = 0; i < fecho.length; i++) {
    const a = fecho[i], b = fecho[(i + 1) % fecho.length];
    const l = Math.hypot(b.x - a.x, b.y - a.y);
    if (l > 0 && vetorial(a, b, p) / l < -LUGAR_IGUAL) return false;
  }
  return true;
}

/** Os limites geográficos de uma imagem: sul e norte em latitude, oeste e leste em longitude (graus). */
export interface LimitesDaImagem { sul: number; oeste: number; norte: number; leste: number }

/**
 * Os limites do terreno do 3D: a caixa das verticais em planta com 30 m de cada lado — ESCOLHA DA TELA, SEM
 * FONTE [esp.terreno-margem] —, no plano local delas (o mesmo da grade). Não se gravam (R3): o Portal os refaz
 * das verticais. Sem vertical com coordenada, NaN.
 */
export function limitesDoTerreno(vs: readonly { latitude: number; longitude: number }[]): LimitesDaImagem {
  const validas = vs.filter((v) => Number.isFinite(v.latitude) && Number.isFinite(v.longitude));
  if (!validas.length) return { sul: NaN, oeste: NaN, norte: NaN, leste: NaN };
  const plano = planoLocal(validas);
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const v of validas) {
    const p = plano.paraPlano(v.latitude, v.longitude);
    minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x); minY = Math.min(minY, p.y); maxY = Math.max(maxY, p.y);
  }
  const so = plano.paraGeo({ x: minX - MARGEM_DO_TERRENO, y: minY - MARGEM_DO_TERRENO });
  const ne = plano.paraGeo({ x: maxX + MARGEM_DO_TERRENO, y: maxY + MARGEM_DO_TERRENO });
  return { sul: so.latitude, oeste: so.longitude, norte: ne.latitude, leste: ne.longitude };
}

/** A vertical do pedido como o motor a lê, numa coluna; sem a coluna, NaN em toda leitura (a vertical continua na grade). */
function verticalDaGrade(v: VerticalNoPedido, c: ColunaDaSecao): VerticalEspacial {
  return { codigo: v.codigo, latitude: v.latitude, longitude: v.longitude, cota: v.cota, profundidade: v.z, valor: v.colunas[c] ?? new Float64Array(v.z.length).fill(NaN) };
}

/**
 * A grade do 3D (pura; o worker a chama): o variograma de cada coluna e o método como na seção, a grade do motor
 * por coluna (`gradeDeVoxels`, margem de meio passo), as zonas estimadas, as superfícies das bocas e dos fundos
 * por IDW em planta [esp.superficies-idw] e o recorte: visível só dentro do fecho convexo das verticais úteis,
 * entre as superfícies e com valor (no SBTn, com zona; no Su, na zona argilosa). Com menos de três verticais
 * fora de uma linha, a frase do R4 e nada visível.
 */
export function calcularGrade(p: PedidoDaGrade): RespostaDaGrade {
  const def = grandezaDaSecao(p.grandeza);
  const principal = colunaPrincipal(p.grandeza);
  const vs = p.verticais.filter((v) => Number.isFinite(v.latitude) && Number.isFinite(v.longitude) && Number.isFinite(v.cota) && v.z.length > 0);
  const noMesmoLugar = gruposNoMesmoLugar(vs);

  // O plano local das verticais úteis: o mesmo que o motor usa na grade (o centroide delas).
  const plano = planoLocal(vs);
  const verticais: VerticalNaGrade[] = vs.map((v) => {
    const q = plano.paraPlano(v.latitude, v.longitude);
    return { codigo: v.codigo, x: q.x, y: q.y, boca: v.cota, fundo: v.cota - v.z[v.z.length - 1] };
  });
  const fecho = fechoConvexo(verticais);

  // Antes de qualquer conta: alinhadas (ou menos de três) e o passo que não serve saem cedo, com a frase deles.
  const passoServe = [p.passo.x, p.passo.y, p.passo.z].every((v) => Number.isFinite(v) && v > 0);
  const cedo = fecho.length < 3 ? FRASE_DAS_ALINHADAS : !passoServe ? FRASE_DO_PASSO : null;
  if (cedo) {
    return {
      grandeza: p.grandeza,
      grade: { origem: { x: NaN, y: NaN, cota: NaN }, passo: { ...p.passo }, n: { x: 0, y: 0, z: 0 }, valores: new Float32Array(0) },
      valores: {}, zonas: null, visivel: new Uint8Array(0), boca: new Float32Array(0), fundo: new Float32Array(0), fecho, verticais,
      variogramas: [], metodoUsado: p.metodo, motivoDoMetodo: null, insuficiente: cedo, noMesmoLugar,
    };
  }

  const variogramas = def.colunas.map((c) => variogramaDaColuna(vs, c, p.modelo));

  let metodoUsado: 'idw' | 'krigagem' = p.metodo;
  let motivoDoMetodo: string | null = null;
  if (p.metodo === 'krigagem') {
    const falha = variogramas.find((v) => !v.ajustado.ajustado);
    if (falha) {
      metodoUsado = 'idw';
      motivoDoMetodo = `krigagem indisponível: ${falha.ajustado.motivo ?? 'o variograma não se ajusta'} — o modelo usa o IDW (potência ${POTENCIA_DO_IDW})`;
    }
  }

  const valores: Record<string, Float32Array> = {};
  let grade: GradeDeVoxels | null = null;
  def.colunas.forEach((c, i) => {
    const g = gradeDeVoxels(vs.map((v) => verticalDaGrade(v, c)), {
      metodo: metodoUsado === 'idw' ? { tipo: 'idw', potencia: POTENCIA_DO_IDW } : { tipo: 'krigagem', variograma: variogramas[i].ajustado },
      passo: p.passo,
    });
    valores[c] = g.valores;
    if (c === principal) grade = g;
  });
  const g = grade as unknown as GradeDeVoxels;
  const { x: nx, y: ny, z: nz } = g.n;
  const total = nx * ny * nz;

  // As superfícies das bocas e dos fundos em planta, por IDW (potência 2) — ESCOLHA DA TELA, SEM FONTE [esp.superficies-idw].
  const pontosDaBoca = verticais.map((v) => ({ p: { x: v.x, y: v.y }, valor: v.boca }));
  const pontosDoFundo = verticais.map((v) => ({ p: { x: v.x, y: v.y }, valor: v.fundo }));
  const boca = new Float32Array(nx * ny).fill(NaN);
  const fundo = new Float32Array(nx * ny).fill(NaN);
  const noFecho = new Uint8Array(nx * ny);
  for (let iy = 0; iy < ny; iy++) {
    for (let ix = 0; ix < nx; ix++) {
      const alvo = { x: g.origem.x + ix * g.passo.x, y: g.origem.y + iy * g.passo.y };
      const s = ix + nx * iy;
      boca[s] = idw(pontosDaBoca, alvo, POTENCIA_DAS_SUPERFICIES);
      fundo[s] = idw(pontosDoFundo, alvo, POTENCIA_DAS_SUPERFICIES);
      noFecho[s] = dentroDoFecho(alvo, fecho) ? 1 : 0;
    }
  }

  const insuficiente = total === 0 ? FRASE_DO_LIMITE_DA_GRADE : null;

  const comZona = p.grandeza === 'sbtn' || p.grandeza === 'su';
  const zonas = comZona ? new Int8Array(total) : null;
  const visivel = new Uint8Array(total);
  if (!insuficiente) {
    const logFr = valores.logFr;
    const logQtn = valores.logQtn;
    const principalValores = valores[principal];
    for (let iz = 0; iz < nz; iz++) {
      const cota = g.origem.cota + iz * g.passo.z;
      for (let iy = 0; iy < ny; iy++) {
        for (let ix = 0; ix < nx; ix++) {
          const s = ix + nx * iy;
          const k = s + nx * ny * iz;
          // A boca e o fundo são Float32: a folga cobre o arredondamento (a cota do nó é dupla precisão).
          const dentro = noFecho[s] === 1 && cota <= boca[s] + FOLGA_DO_FLOAT32 && cota >= fundo[s] - FOLGA_DO_FLOAT32;
          if (!dentro) continue;
          let z = 0;
          if (zonas && logFr && logQtn && Number.isFinite(logFr[k]) && Number.isFinite(logQtn[k])) z = zonaSbtn(10 ** logFr[k], 10 ** logQtn[k]);
          const comValor = p.grandeza === 'sbtn' ? z > 0 : Number.isFinite(principalValores[k]) && (p.grandeza !== 'su' || zonaArgilosa(z));
          if (!comValor) continue;
          visivel[k] = 1;
          if (zonas) zonas[k] = z;
        }
      }
    }
  }

  return {
    grandeza: p.grandeza, grade: g, valores, zonas, visivel, boca, fundo, fecho, verticais, variogramas, metodoUsado, motivoDoMetodo,
    insuficiente, noMesmoLugar,
  };
}

/* ── A superfície do nível d'água no 3D (onda 347, spec 2026-10-22 §4.3) ── */

/** Quantas verticais com NA a superfície pede — ESCOLHA DA TELA, SEM FONTE [3d.na-minimo]. */
export const NA_MINIMO_DE_VERTICAIS = 3; // ESCOLHA DA TELA, SEM FONTE [3d.na-minimo]

/** A superfície do NA: a cota em cada nó em planta (nx × ny, índice ix + nx·iy, NaN fora do fecho), ou por que não há. */
export type SuperficieDoNa =
  | { cotas: Float32Array; usadas: string[]; metodoUsado: 'idw' | 'krigagem'; motivoDoMetodo: string | null }
  | { motivo: string };

/** A frase de quando faltam verticais com NA. */
function fraseDoNaMinimo(n: number): string {
  const pede = `a superfície pede ${numeroBr(NA_MINIMO_DE_VERTICAIS, 0)}`;
  if (n === 0) return `nenhuma vertical tem NA; ${pede}`;
  return `o NA está em ${numeroBr(n, 0)} ${n === 1 ? 'vertical' : 'verticais'}; ${pede}`;
}

/**
 * A superfície do nível d'água (pura): a cota do NA em cada vertical da grade = boca − NA (o NA da premissa da vertical, m
 * abaixo da boca); entre as verticais, o método do modelo (`metodo`, o `metodoUsado` da grade) — a krigagem com o variograma
 * das cotas do NA em planta, e o IDW das superfícies (`POTENCIA_DAS_SUPERFICIES`) quando a krigagem não serve, com o motivo —,
 * só nos nós dentro do fecho da grade. O NA que não serve fica fora: sem número, acima da boca (negativo) ou abaixo do fundo
 * da vertical. Com menos de `NA_MINIMO_DE_VERTICAIS` que servem, não há superfície, e a frase diz por quê. Estimada entre as
 * verticais, como o resto do modelo (fora do RN-252).
 */
export function superficieDoNa(
  g: Pick<RespostaDaGrade, 'grade' | 'verticais' | 'fecho' | 'insuficiente'>,
  nas: readonly { codigo: string; na: number | null }[],
  metodo: 'idw' | 'krigagem',
  modelo: ModeloDeVariograma = 'esferico',
): SuperficieDoNa {
  if (g.insuficiente) return { motivo: g.insuficiente };
  const naDe = new Map(nas.map((x) => [x.codigo, x.na]));
  const usadas: string[] = [];
  const pontos: { p: PontoPlano; valor: number }[] = [];
  for (const v of g.verticais) {
    const na = naDe.get(v.codigo);
    if (typeof na !== 'number' || !Number.isFinite(na) || na < 0 || na > v.boca - v.fundo + FOLGA_DO_FLOAT32) continue;
    usadas.push(v.codigo);
    pontos.push({ p: { x: v.x, y: v.y }, valor: v.boca - na });
  }
  if (usadas.length < NA_MINIMO_DE_VERTICAIS) return { motivo: fraseDoNaMinimo(usadas.length) };

  const { origem, passo, n } = g.grade;
  const alvos: (PontoPlano | null)[] = [];
  for (let iy = 0; iy < n.y; iy++) {
    for (let ix = 0; ix < n.x; ix++) {
      const alvo = { x: origem.x + ix * passo.x, y: origem.y + iy * passo.y };
      alvos.push(dentroDoFecho(alvo, g.fecho) ? alvo : null);
    }
  }
  const pelo = (f: (alvo: PontoPlano) => number): Float32Array => Float32Array.from(alvos, (a) => (a ? f(a) : NaN));
  const porIdw = (): Float32Array => pelo((a) => idw(pontos, a, POTENCIA_DAS_SUPERFICIES));

  if (metodo === 'krigagem') {
    const ajustado = ajustarVariograma(variogramaExperimental(pontos, CLASSES_DO_VARIOGRAMA), modelo);
    let falha = ajustado.ajustado ? null : ajustado.motivo ?? 'o variograma não se ajusta';
    if (!falha) {
      const cotas = pelo((a) => krigagemOrdinaria(pontos, a, ajustado).valor);
      if (alvos.every((a, i) => !a || Number.isFinite(cotas[i]))) return { cotas, usadas, metodoUsado: 'krigagem', motivoDoMetodo: null };
      falha = 'o sistema da krigagem não tem solução';
    }
    return {
      cotas: porIdw(), usadas, metodoUsado: 'idw',
      motivoDoMetodo: `krigagem indisponível: ${falha} — a superfície do NA usa o IDW (potência ${POTENCIA_DAS_SUPERFICIES})`,
    };
  }
  return { cotas: porIdw(), usadas, metodoUsado: 'idw', motivoDoMetodo: null };
}

/* ── As camadas geotécnicas no 3D (onda 347, spec 2026-10-22 §4.6) ───────── */

/** Uma camada de uma vertical como o 3D a lê: topo e base em m abaixo da boca, e a unidade (o texto do engenheiro ou a zona da sugestão). */
export interface CamadaNoModelo { topo: number; base: number; unidade: string }

/** As unidades no 3D: os nomes (da mais rasa à mais funda), a unidade de cada nó (−1 = vazio), e quem tem camada. */
export interface UnidadesNaGrade {
  /** Os nomes mostrados (a grafia mais frequente de cada unidade), da que aparece mais rasa à mais funda. */
  unidades: string[];
  /** Uma entrada por nó da grade (o índice da grade): a posição em `unidades`, ou −1 (fora do visível, ou sem vertical que vote). */
  indice: Int16Array;
  /** Quantas verticais da grade têm alguma camada. */
  comCamada: number;
  /** As verticais da grade sem camada (pelo código, na ordem da grade): não votam no indicador. */
  semCamada: string[];
}

/** A chave de uma unidade: sem maiúsculas e sem os espaços das pontas («Argila mole» e «argila mole » são a mesma). */
export const normalizarUnidade = (u: string): string => u.trim().toLocaleLowerCase('pt-BR');

/** A folga de profundidade de uma camada (m): a cota do nó no topo ou na base dela. */
const FOLGA_DA_CAMADA = 1e-6;

/** Os pesos de cada ponto em cada alvo (alvo × ponto), pelo IDW das superfícies; o ponto no mesmo lugar do alvo leva tudo. */
function pesosPeloIdw(pontos: readonly PontoPlano[], alvos: readonly PontoPlano[]): Float64Array {
  const n = pontos.length;
  const w = new Float64Array(alvos.length * n);
  alvos.forEach((a, s) => {
    const iguais = pontos.map((q, j) => (Math.hypot(q.x - a.x, q.y - a.y) <= LUGAR_IGUAL ? j : -1)).filter((j) => j >= 0);
    if (iguais.length) { for (const j of iguais) w[s * n + j] = 1 / iguais.length; return; }
    let soma = 0;
    for (let j = 0; j < n; j++) { const d = Math.pow(Math.hypot(pontos[j].x - a.x, pontos[j].y - a.y), -POTENCIA_DAS_SUPERFICIES); w[s * n + j] = d; soma += d; }
    for (let j = 0; j < n; j++) w[s * n + j] /= soma;
  });
  return w;
}

/**
 * Os pesos da krigagem ordinária de cada ponto em cada alvo (alvo × ponto), com o variograma dado: a matriz (n + 1) × (n + 1)
 * invertida uma vez (Gauss–Jordan com pivô) e, em cada alvo, os pesos = A⁻¹ · γ₀. A estimativa é linear nos valores: o mesmo
 * peso vale para o indicador de cada unidade. Null quando a matriz não se inverte (pontos no mesmo lugar, variograma sem número).
 */
function pesosPelaKrigagem(pontos: readonly PontoPlano[], alvos: readonly PontoPlano[], v: Variograma): Float64Array | null {
  const n = pontos.length, m = n + 1;
  if (![v.pepita, v.patamar, v.alcance].every(Number.isFinite)) return null;
  const a: Float64Array[] = Array.from({ length: m }, () => new Float64Array(2 * m));
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) a[i][j] = i === j ? 0 : gamaDoModelo(v, Math.hypot(pontos[i].x - pontos[j].x, pontos[i].y - pontos[j].y));
    a[i][n] = 1; a[n][i] = 1;
  }
  for (let i = 0; i < m; i++) a[i][m + i] = 1;
  for (let c = 0; c < m; c++) {
    let piv = c;
    for (let r = c + 1; r < m; r++) if (Math.abs(a[r][c]) > Math.abs(a[piv][c])) piv = r;
    if (!(Math.abs(a[piv][c]) > 1e-12)) return null;
    [a[c], a[piv]] = [a[piv], a[c]];
    const p = a[c][c];
    for (let k = 0; k < 2 * m; k++) a[c][k] /= p;
    for (let r = 0; r < m; r++) {
      if (r === c || a[r][c] === 0) continue;
      const f = a[r][c];
      for (let k = 0; k < 2 * m; k++) a[r][k] -= f * a[c][k];
    }
  }
  const w = new Float64Array(alvos.length * n);
  const g0 = new Float64Array(m);
  for (let s = 0; s < alvos.length; s++) {
    const alvo = alvos[s];
    const igual = pontos.findIndex((q) => Math.hypot(q.x - alvo.x, q.y - alvo.y) <= LUGAR_IGUAL);
    if (igual >= 0) { w[s * n + igual] = 1; continue; }
    for (let j = 0; j < n; j++) g0[j] = gamaDoModelo(v, Math.hypot(pontos[j].x - alvo.x, pontos[j].y - alvo.y));
    g0[n] = 1;
    for (let j = 0; j < n; j++) {
      let x = 0;
      for (let k = 0; k < m; k++) x += a[j][m + k] * g0[k];
      if (!Number.isFinite(x)) return null;
      w[s * n + j] = x;
    }
  }
  return w;
}

/**
 * As unidades geotécnicas nos nós da grade (pura; spec §4.6): em cada vertical com camada e em cada cota que as camadas dela
 * cobrem, o indicador de cada unidade (1 se a cota está numa camada dela, 0 se não); entre as verticais, o indicador passa pelo
 * método do modelo (`metodo`: o IDW das superfícies, `POTENCIA_DAS_SUPERFICIES`; ou a krigagem com o variograma da coluna
 * principal da grade, que vira IDW quando ele não se ajusta ou o sistema não tem solução), e vence a unidade de maior
 * indicador; no empate, a mais rasa — ESCOLHA DA TELA, SEM FONTE [3d.camadas-indicador]. A vertical sem camada (e a cota fora
 * das camadas de uma vertical) não vota: não é «0». Nó fora do visível da grade, ou sem nenhuma vertical que vote naquela cota,
 * fica vazio (−1). As unidades se comparam por `normalizarUnidade`; o nome mostrado é a grafia mais frequente. Estimado entre as
 * verticais, como o resto do modelo (fora do RN-252).
 */
export function unidadesNaGrade(
  g: Pick<RespostaDaGrade, 'grade' | 'verticais' | 'visivel'> & Partial<Pick<RespostaDaGrade, 'variogramas' | 'grandeza'>>,
  camadas: ReadonlyMap<string, readonly CamadaNoModelo[]>,
  metodo: 'idw' | 'krigagem',
): UnidadesNaGrade {
  const { origem, passo, n } = g.grade;
  const total = n.x * n.y * n.z;
  const indice = new Int16Array(total).fill(-1);
  // As verticais da grade com camada (as que valem: topo < base, com número); as outras não votam.
  const comCamadas: { v: VerticalNaGrade; cs: { topo: number; base: number; chave: string }[] }[] = [];
  const semCamada: string[] = [];
  const grafias = new Map<string, Map<string, number>>();
  const maisRasa = new Map<string, number>();
  for (const v of g.verticais) {
    const cs = (camadas.get(v.codigo) ?? [])
      .filter((c) => Number.isFinite(c.topo) && Number.isFinite(c.base) && c.topo < c.base && typeof c.unidade === 'string' && c.unidade.trim() !== '')
      .map((c) => ({ topo: c.topo, base: c.base, chave: normalizarUnidade(c.unidade), nome: c.unidade.trim() }))
      .sort((a, b) => a.topo - b.topo);
    if (!cs.length) { semCamada.push(v.codigo); continue; }
    for (const c of cs) {
      const contas = grafias.get(c.chave) ?? new Map<string, number>();
      contas.set(c.nome, (contas.get(c.nome) ?? 0) + 1);
      grafias.set(c.chave, contas);
      maisRasa.set(c.chave, Math.max(maisRasa.get(c.chave) ?? -Infinity, v.boca - c.topo));
    }
    comCamadas.push({ v, cs });
  }
  // A ordem: a unidade que aparece mais alta (a maior cota de topo) primeiro; o desempate pela chave.
  const chaves = [...grafias.keys()].sort((a, b) => (maisRasa.get(b)! - maisRasa.get(a)!) || a.localeCompare(b, 'pt-BR'));
  const unidades = chaves.map((k) => {
    let melhor = '', vezes = -1;
    for (const [nome, x] of grafias.get(k)!) if (x > vezes) { melhor = nome; vezes = x; }
    return melhor;
  });
  const resposta = { unidades, indice, comCamada: comCamadas.length, semCamada };
  if (!comCamadas.length || !chaves.length || total === 0) return resposta;
  const posicao = new Map(chaves.map((k, i) => [k, i]));

  // Os alvos em planta: os nós com algum visível (os outros não precisam de peso).
  const nPlano = n.x * n.y;
  const alvos: PontoPlano[] = [];
  const noAlvo = new Int32Array(nPlano).fill(-1);
  for (let s = 0; s < nPlano; s++) {
    let algum = false;
    for (let iz = 0; iz < n.z && !algum; iz++) algum = g.visivel[s + nPlano * iz] === 1;
    if (!algum) continue;
    noAlvo[s] = alvos.length;
    alvos.push({ x: origem.x + (s % n.x) * passo.x, y: origem.y + Math.floor(s / n.x) * passo.y });
  }
  const variograma = metodo === 'krigagem'
    ? g.variogramas?.find((x) => x.coluna === (g.grandeza ? colunaPrincipal(g.grandeza) : undefined))?.ajustado ?? null : null;
  // Os pesos por conjunto de verticais que votam (o conjunto muda só nas cotas onde uma vertical começa ou acaba de ter camada).
  // Os pesos do conjunto de verticais que votam: o conjunto muda só nas cotas onde uma vertical começa ou acaba de ter camada, e o
  // laço anda em `iz` em ordem — cada conjunto vale num trecho contínuo, e só o último fica guardado (a memória de um, não de todos).
  let ultimo: { chave: string; w: Float64Array } | null = null;
  const pesos = (quem: readonly number[]): Float64Array => {
    const chave = quem.join(',');
    if (ultimo?.chave !== chave) {
      const pontos = quem.map((i) => ({ x: comCamadas[i].v.x, y: comCamadas[i].v.y }));
      ultimo = { chave, w: (variograma?.ajustado ? pesosPelaKrigagem(pontos, alvos, variograma) : null) ?? pesosPeloIdw(pontos, alvos) };
    }
    return ultimo.w;
  };

  const nu = chaves.length;
  const soma = new Float64Array(nu);
  for (let iz = 0; iz < n.z; iz++) {
    const cota = origem.cota + iz * passo.z;
    // Quem vota nesta cota e a unidade de cada uma (o indicador é 1 nela e 0 nas outras). A cota fora das camadas da vertical —
    // acima da primeira, abaixo da última ou num buraco entre duas — não vota.
    const quem: number[] = [];
    const unidadeDe: number[] = [];
    comCamadas.forEach(({ v, cs }, i) => {
      const d = v.boca - cota;
      // [topo, base), e a base da última fecha a vertical.
      const c = cs.find((k, j) => d >= k.topo - FOLGA_DA_CAMADA && (d < k.base - FOLGA_DA_CAMADA || (j === cs.length - 1 && d <= k.base + FOLGA_DA_CAMADA)));
      if (!c) return;
      quem.push(i);
      unidadeDe.push(posicao.get(c.chave)!);
    });
    if (!quem.length) continue;
    const w = pesos(quem);
    const nq = quem.length;
    for (let s = 0; s < nPlano; s++) {
      const k = s + nPlano * iz;
      if (g.visivel[k] !== 1 || noAlvo[s] < 0) continue;
      soma.fill(0);
      const base = noAlvo[s] * nq;
      for (let j = 0; j < nq; j++) {
        const wj = w[base + j];
        if (wj !== 0) soma[unidadeDe[j]] += wj;
      }
      // O maior indicador; no empate (a folga do arredondamento), a mais rasa (a primeira da lista).
      let melhor = -1, maior = 1e-9;
      for (let u = 0; u < nu; u++) if (soma[u] > maior + 1e-9) { melhor = u; maior = soma[u]; }
      indice[k] = melhor;
    }
  }
  return resposta;
}

/**
 * A zona SBTn predominante de cada unidade (pela chave `normalizarUnidade`): a mais frequente entre as leituras medidas das
 * verticais dentro das camadas dela; no empate, a que aparece mais rasa; null sem leitura com zona. Dá a cor da unidade, para
 * casar com as colunas (§4.6).
 */
export function zonasDasUnidades(
  camadas: ReadonlyMap<string, readonly CamadaNoModelo[]>,
  verticais: readonly { codigo: string; z: ArrayLike<number>; zonaMedida: ArrayLike<number> }[],
): Map<string, number | null> {
  const contas = new Map<string, Map<number, { n: number; primeira: number }>>();
  for (const v of verticais) {
    for (const c of camadas.get(v.codigo) ?? []) {
      const chave = normalizarUnidade(c.unidade);
      const m = contas.get(chave) ?? new Map<number, { n: number; primeira: number }>();
      contas.set(chave, m);
      for (let i = 0; i < v.z.length; i++) {
        const z = v.z[i], zona = v.zonaMedida[i];
        if (!(z >= c.topo - FOLGA_DA_CAMADA && z < c.base - FOLGA_DA_CAMADA) || !(zona >= 1 && zona <= 9)) continue;
        const x = m.get(zona);
        if (x) { x.n++; x.primeira = Math.min(x.primeira, z); } else m.set(zona, { n: 1, primeira: z });
      }
    }
  }
  const saida = new Map<string, number | null>();
  for (const [chave, m] of contas) {
    let melhor: number | null = null, maior = { n: 0, primeira: Infinity };
    for (const [zona, x] of m) if (x.n > maior.n || (x.n === maior.n && x.primeira < maior.primeira)) { melhor = zona; maior = x; }
    saida.set(chave, melhor);
  }
  return saida;
}

/* ── A fatia em planta: a grade numa cota, para o 2D (Tarefa 316.3, passos 6–10) ── */

/**
 * A grade do 3D numa cota, como a seção 2D a desenha em planta (o modo planta da `SecaoEspacial`, o caminho sem
 * WebGL): as colunas de oeste a leste, as linhas de NORTE a sul (a linha 0 é a de cima), os valores e as zonas no
 * índice linha·nx + coluna, o `pintar` = o visível da grade nessa cota, e as verticais na célula delas com a zona
 * MEDIDA nessa cota (a da leitura; 0 sem leitura) — medido e estimado nunca se confundem.
 */
export interface PlantaNaCota {
  /** A cota da fatia: a do nó mais perto da pedida [esp.planta-no]; NaN sem grade. */
  cota: number;
  /** m para leste, os centros das colunas (os nós da grade). */
  x: Float64Array;
  /** m para norte, de cima (norte) para baixo (sul). */
  y: Float64Array;
  valores: Record<string, Float32Array>;
  zonas: Int8Array | null;
  pintar: Uint8Array;
  verticais: { codigo: string; linha: number; coluna: number; zonaMedida: number }[];
  /** A grandeza da grade (a seção em planta escolhe a cor por ela). */
  grandeza: GrandezaDaSecao;
}

/**
 * A fatia em planta da grade na cota (pura): o nó de cota mais perto da pedida — ESCOLHA DA TELA, SEM FONTE
 * [esp.planta-no] —, a mesma que a fatia em planta do 3D. A zona medida de cada vertical é a da leitura mais perto
 * da profundidade (boca − cota), só dentro do trecho medido, com meia célula de folga; fora dele, 0. Sem grade (as
 * alinhadas, o limite), a planta vazia.
 */
export function fatiaEmPlanta(g: RespostaDaGrade, vs: readonly VerticalNoPedido[], cota: number): PlantaNaCota {
  const { origem, passo, n } = g.grade;
  if (!n.x || !n.y || !n.z || !Number.isFinite(cota)) {
    return { cota: NaN, x: new Float64Array(0), y: new Float64Array(0), valores: {}, zonas: null, pintar: new Uint8Array(0), verticais: [], grandeza: g.grandeza };
  }
  const iz = Math.min(n.z - 1, Math.max(0, Math.round((cota - origem.cota) / passo.z)));
  const cotaDoNo = origem.cota + iz * passo.z;
  const x = Float64Array.from({ length: n.x }, (_, ix) => origem.x + ix * passo.x);
  const y = Float64Array.from({ length: n.y }, (_, linha) => origem.y + (n.y - 1 - linha) * passo.y);
  const total = n.x * n.y;
  const no = (coluna: number, linha: number): number => coluna + n.x * ((n.y - 1 - linha) + n.y * iz);
  const valores: Record<string, Float32Array> = {};
  for (const [c, serie] of Object.entries(g.valores)) {
    const v = new Float32Array(total);
    for (let linha = 0; linha < n.y; linha++) for (let coluna = 0; coluna < n.x; coluna++) v[linha * n.x + coluna] = serie[no(coluna, linha)];
    valores[c] = v;
  }
  const zonas = g.zonas ? new Int8Array(total) : null;
  const pintar = new Uint8Array(total);
  for (let linha = 0; linha < n.y; linha++) {
    for (let coluna = 0; coluna < n.x; coluna++) {
      const k = no(coluna, linha);
      pintar[linha * n.x + coluna] = g.visivel[k];
      if (zonas && g.zonas) zonas[linha * n.x + coluna] = g.zonas[k];
    }
  }
  const porCodigo = new Map(vs.map((v) => [v.codigo, v]));
  const verticais = g.verticais.map((gv) => {
    const coluna = Math.min(n.x - 1, Math.max(0, Math.round((gv.x - origem.x) / passo.x)));
    const linha = n.y - 1 - Math.min(n.y - 1, Math.max(0, Math.round((gv.y - origem.y) / passo.y)));
    const v = porCodigo.get(gv.codigo);
    let zonaMedida = 0;
    if (v && v.z.length) {
      const prof = v.cota - cotaDoNo;
      const folga = passo.z / 2;
      if (prof >= v.z[0] - folga && prof <= v.z[v.z.length - 1] + folga) {
        const i = indiceNaProfundidade(v.z, prof);
        zonaMedida = i >= 0 ? v.zonaMedida[i] ?? 0 : 0;
      }
    }
    return { codigo: gv.codigo, linha, coluna, zonaMedida };
  });
  return { cota: cotaDoNo, x, y, valores, zonas, pintar, verticais, grandeza: g.grandeza };
}

/**
 * A leitura de uma célula da planta: x e y (m), a cota, se está pintada, a vertical da célula com a zona MEDIDA
 * (a primeira, se mais de uma cai nela), a zona estimada e o valor de cada coluna.
 */
export function leituraNaPlanta(p: PlantaNaCota, coluna: number, linha: number): {
  x: number; y: number; cota: number; pintada: boolean; medida: { codigo: string; zona: number } | null; zona: number;
  valores: Record<string, number>;
} {
  const nx = p.x.length;
  const dentro = coluna >= 0 && coluna < nx && linha >= 0 && linha < p.y.length;
  const k = linha * nx + coluna;
  const v = dentro ? p.verticais.find((w) => w.coluna === coluna && w.linha === linha) ?? null : null;
  const valores: Record<string, number> = {};
  for (const c of Object.keys(p.valores)) valores[c] = dentro ? p.valores[c][k] : NaN;
  return {
    x: dentro ? p.x[coluna] : NaN, y: dentro ? p.y[linha] : NaN, cota: p.cota, pintada: dentro ? p.pintar[k] === 1 : false,
    medida: v ? { codigo: v.codigo, zona: v.zonaMedida } : null, zona: dentro && p.zonas ? p.zonas[k] : 0, valores,
  };
}

/* ── Os passos de partida (R9) ─────────────────────────────────────────── */

/** O número no padrão da casa, para as frases daqui (os avisos levam metros inteiros). */
const metros = (v: number): string => `${Number.isInteger(v) ? String(v) : String(v).replace('.', ',')} m`;

/**
 * Os passos de partida: na seção, o menor da série em que as colunas do motor (⌊comprimento/passo⌋ + 1, mais a
 * do fim quando falta mais de 1 % do passo) não passam de 400 — ESCOLHA DA TELA, SEM FONTE [esp.secao-passos];
 * na grade, o menor em que as células do lado maior, sem a meia célula de margem (⌈extensão/passo⌉), não
 * passam de 60 — [esp.grade-passos]; nenhum cabe: o maior, com o aviso; no celular, o dobro [esp.celular-dobro].
 */
export function passosPadrao(x: { comprimento?: number; extensao?: { x: number; y: number }; celular: boolean }):
{ secao: { horizontal: number; vertical: number }; grade: { x: number; y: number; z: number }; aviso: string | null } {
  const fator = x.celular ? FATOR_DO_CELULAR : 1;
  const avisos: string[] = [];
  let horizontal: number = PASSOS_DA_SECAO[1];
  if (x.comprimento !== undefined && Number.isFinite(x.comprimento) && x.comprimento > 0) {
    const L = x.comprimento;
    const cabe = PASSOS_DA_SECAO.find((passo) => {
      const regulares = Math.floor(L / passo + FOLGA) + 1;
      const faltaFim = L - (regulares - 1) * passo > 0.01 * passo;
      return regulares + (faltaFim ? 1 : 0) <= COLUNAS_NO_MAXIMO;
    });
    horizontal = cabe ?? PASSOS_DA_SECAO[PASSOS_DA_SECAO.length - 1];
    // O aviso diz o passo que a tela usa: no celular, já o dobro (M2 da revisão da 316.2).
    if (!cabe) avisos.push(`a linha é longa: passo de ${metros(horizontal * fator)}`);
  }
  let passoDaGrade: number = PASSOS_DA_GRADE[1];
  if (x.extensao && Number.isFinite(x.extensao.x) && Number.isFinite(x.extensao.y)) {
    const maior = Math.max(x.extensao.x, x.extensao.y, 0);
    const cabe = PASSOS_DA_GRADE.find((passo) => Math.ceil(maior / passo - FOLGA) <= CELULAS_NO_MAXIMO);
    passoDaGrade = cabe ?? PASSOS_DA_GRADE[PASSOS_DA_GRADE.length - 1];
    if (!cabe) avisos.push(`a grade é grande: passo de ${metros(passoDaGrade * fator)}`);
  }
  return {
    secao: { horizontal: horizontal * fator, vertical: PASSO_VERTICAL_DA_SECAO * fator },
    grade: { x: passoDaGrade * fator, y: passoDaGrade * fator, z: PASSO_VERTICAL_DA_GRADE * fator },
    aviso: avisos.length ? avisos.join('; ') : null,
  };
}

/* ── As isolinhas (marching squares) ───────────────────────────────────── */

/**
 * As isolinhas de um campo em grade (nx colunas × ny linhas, linha a linha) no nível dado, por *marching
 * squares* nos centros das células: os segmentos em índice fracionário de coluna (x) e de linha (y). O
 * quadrado com um canto NaN, ou fora do `pintar`, não traça nada — a isolinha quebra ali.
 */
export function isolinhas(valores: ArrayLike<number>, nx: number, ny: number, nivel: number, pintar?: Uint8Array):
{ x1: number; y1: number; x2: number; y2: number }[] {
  const saida: { x1: number; y1: number; x2: number; y2: number }[] = [];
  if (!Number.isFinite(nivel) || nx < 2 || ny < 2) return saida;
  const em = (i: number, j: number): number => {
    const k = j * nx + i;
    if (pintar && !pintar[k]) return NaN;
    return valores[k];
  };
  // O ponto da aresta entre dois cantos onde o campo cruza o nível.
  const cruza = (xa: number, ya: number, va: number, xb: number, yb: number, vb: number): [number, number] => {
    const t = vb === va ? 0.5 : (nivel - va) / (vb - va);
    return [xa + t * (xb - xa), ya + t * (yb - ya)];
  };
  for (let j = 0; j + 1 < ny; j++) {
    for (let i = 0; i + 1 < nx; i++) {
      const v0 = em(i, j), v1 = em(i + 1, j), v2 = em(i + 1, j + 1), v3 = em(i, j + 1);
      if (!Number.isFinite(v0) || !Number.isFinite(v1) || !Number.isFinite(v2) || !Number.isFinite(v3)) continue;
      const caso = (v0 >= nivel ? 1 : 0) | (v1 >= nivel ? 2 : 0) | (v2 >= nivel ? 4 : 0) | (v3 >= nivel ? 8 : 0);
      if (caso === 0 || caso === 15) continue;
      const topo = () => cruza(i, j, v0, i + 1, j, v1);
      const direita = () => cruza(i + 1, j, v1, i + 1, j + 1, v2);
      const base = () => cruza(i, j + 1, v3, i + 1, j + 1, v2);
      const esquerda = () => cruza(i, j, v0, i, j + 1, v3);
      const liga = (a: [number, number], b: [number, number]) => saida.push({ x1: a[0], y1: a[1], x2: b[0], y2: b[1] });
      switch (caso) {
        case 1: case 14: liga(esquerda(), topo()); break;
        case 2: case 13: liga(topo(), direita()); break;
        case 3: case 12: liga(esquerda(), direita()); break;
        case 4: case 11: liga(direita(), base()); break;
        case 6: case 9: liga(topo(), base()); break;
        case 7: case 8: liga(esquerda(), base()); break;
        case 5: {
          // A sela (v0 e v2 acima): desempata pelo centro. Centro acima, os cantos de cima se ligam pelo meio e quem
          // fica isolado são os de baixo, v1 e v3 (topo–direita, esquerda–base); centro abaixo, ficam isolados v0 e v2
          // (M1 da revisão da 316.2: estava trocado).
          const centro = (v0 + v1 + v2 + v3) / 4;
          if (centro >= nivel) { liga(topo(), direita()); liga(esquerda(), base()); } else { liga(esquerda(), topo()); liga(direita(), base()); }
          break;
        }
        case 10: {
          // A sela (v1 e v3 acima): centro acima, isolados v0 e v2; centro abaixo, isolados v1 e v3.
          const centro = (v0 + v1 + v2 + v3) / 4;
          if (centro >= nivel) { liga(esquerda(), topo()); liga(direita(), base()); } else { liga(topo(), direita()); liga(esquerda(), base()); }
          break;
        }
        default: break;
      }
    }
  }
  return saida;
}

/* ── As classes da escala (R5) ─────────────────────────────────────────── */

/** O número da série 1–2–5 × 10ⁿ imediatamente ≥ x (x > 0). */
function daSerie125Acima(x: number): number {
  const e = Math.floor(Math.log10(x) - FOLGA);
  for (const m of [1, 2, 5, 10]) {
    const v = Number((m * 10 ** e).toPrecision(12));
    if (v >= x * (1 - 1e-9)) return v;
  }
  return Number((10 ** (e + 1)).toPrecision(12));
}

/** O número da série 1–2–5 × 10ⁿ imediatamente ≤ x (x > 0). */
function daSerie125Abaixo(x: number): number {
  const e = Math.floor(Math.log10(x) + FOLGA);
  let melhor = Number((10 ** e).toPrecision(12));
  for (const m of [2, 5]) {
    const v = Number((m * 10 ** e).toPrecision(12));
    if (v <= x * (1 + 1e-9)) melhor = v;
  }
  return melhor;
}

/** O seguinte na série 1–2–5 × 10ⁿ. */
function seguinteNaSerie125(v: number): number {
  const e = Math.floor(Math.log10(v) + FOLGA);
  const m = Math.round(v / 10 ** e);
  return Number(((m === 1 ? 2 : m === 2 ? 5 : 10) * 10 ** e).toPrecision(12));
}

/**
 * Os limites das classes da escala — exatamente 8 (7 classes). Linear: o menor passo da série 1–2–5 × 10ⁿ em
 * que ⌊mín/passo⌋·passo + 7·passo ≥ máx; os limites ⌊mín/passo⌋·passo + k·passo. Log: 8 valores seguidos da
 * série 1–2–5 a partir do maior ≤ mín; se não chegam ao máx, por décadas; se ainda não, a última classe fica
 * aberta (a legenda escreve «≥ …»). Domínio sem largura (mín = máx) ou sem valor: [mín] — uma classe só.
 */
export function classesDaEscala(dominio: [number, number], tipo: EscalaDoEixo): number[] {
  let [lo, hi] = dominio;
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return [Number.isFinite(lo) ? lo : Number.isFinite(hi) ? hi : NaN];
  if (hi < lo) [lo, hi] = [hi, lo];
  // Domínio estreitíssimo (um campo quase constante) é domínio sem largura: as classes sairiam repetidas pelo arredondamento
  // (M4 da revisão da 316.2).
  if (hi - lo <= 1e-9 * Math.max(Math.abs(lo), Math.abs(hi)) || hi - lo < 1e-12) return [lo];
  if (tipo === 'log' && lo > 0) {
    const limites: number[] = [daSerie125Abaixo(lo)];
    while (limites.length < 8) limites.push(seguinteNaSerie125(limites[limites.length - 1]));
    if (limites[7] >= hi * (1 - 1e-9)) return limites;
    const decadas: number[] = [Number((10 ** Math.floor(Math.log10(lo) + FOLGA)).toPrecision(12))];
    while (decadas.length < 8) decadas.push(Number((decadas[decadas.length - 1] * 10).toPrecision(12)));
    return decadas;
  }
  // Linear (e o log com mín ≤ 0, que não tem escala log).
  let passo = daSerie125Acima((hi - lo) / 7);
  for (let volta = 0; volta < 40; volta++) {
    const inicio = Math.floor(lo / passo + FOLGA) * passo;
    if (inicio + 7 * passo >= hi * (1 - 1e-9) - FOLGA) break;
    passo = seguinteNaSerie125(passo);
  }
  const inicio = Math.floor(lo / passo + FOLGA) * passo;
  const casas = Math.max(0, -Math.floor(Math.log10(passo)) + 1);
  return Array.from({ length: 8 }, (_, k) => {
    const v = Number((inicio + k * passo).toFixed(Math.min(casas, 20)));
    return v === 0 ? 0 : v;
  });
}

/** A classe (1–7) de um valor pelos limites; 0 sem valor (NaN; ≤ 0 numa escala log, cujos limites são > 0); uma classe só: 1. */
export function classeDoValor(v: number, limites: readonly number[]): number {
  if (!Number.isFinite(v) || !limites.length) return 0;
  if (limites.length === 1) return 1;
  if (limites[0] > 0 && !(v > 0)) return 0;
  const n = limites.length - 1;
  if (v >= limites[n]) return n;
  for (let k = 1; k <= n; k++) if (v < limites[k]) return k;
  return n;
}

/* ── A leitura de uma célula ───────────────────────────────────────────── */

/**
 * A leitura de uma célula da seção: a distância e a cota, se está pintada, a zona medida quando a coluna é a
 * faixa de uma estação (o estimado e o medido nunca se confundem), a zona estimada e o valor de cada coluna.
 */
export function leituraNaSecao(r: RespostaDaSecao, coluna: number, linha: number): {
  distancia: number; cota: number; pintada: boolean; medida: { codigo: string; zona: number } | null; zona: number;
  valores: Record<string, number>;
} {
  const nd = r.secao.distancias.length;
  const k = linha * nd + coluna;
  const dentro = coluna >= 0 && coluna < nd && linha >= 0 && linha < r.secao.cotas.length;
  const estacao = dentro ? r.estacoes.find((e) => e.coluna === coluna) ?? null : null;
  const valores: Record<string, number> = {};
  for (const c of Object.keys(r.valores)) valores[c] = dentro ? r.valores[c][k] : NaN;
  return {
    distancia: dentro ? r.secao.distancias[coluna] : NaN,
    cota: dentro ? r.secao.cotas[linha] : NaN,
    pintada: dentro ? r.pintar[k] === 1 : false,
    medida: estacao ? { codigo: estacao.codigo, zona: estacao.medida[linha] ?? 0 } : null,
    zona: dentro && r.zonas ? r.zonas[k] : 0,
    valores,
  };
}

/* ── As sobreposições das verticais (pedido do Humberto, 16/10/2026) ───── */

/** O que se põe sobre a seção (ou no 3D) em cada vertical: o perfil medido de uma grandeza, ou a zona SBT/SBTn. */
export type GrandezaDaSobreposicao = 'qc' | 'qt' | 'fs' | 'u2' | 'rf' | 'ic' | 'qtn' | 'su' | 'sbt' | 'sbtn';

/** Uma grandeza da sobreposição: o rótulo, a unidade, a coluna do motor e se é série (o traço) ou zona (a cor). */
export interface DefinicaoDaSobreposicao {
  chave: GrandezaDaSobreposicao;
  rotulo: string;
  unidade: string;
  tipo: 'serie' | 'zona';
  /** A coluna do motor (para o valor e a marca de homologação). */
  coluna: ColunaDoMotor;
  /** O domínio fixo da faixa (o Ic de 1 a 4, como a trilha dele); sem ele, o dos dados, com o zero. */
  dominio?: readonly [number, number];
}

/**
 * As grandezas que se sobrepõem — as mesmas, com os mesmos rótulos e unidades, das trilhas da vertical (313/315):
 * qc, qt, fs, u₂, Rf, Ic, Qtn e Su em série; a zona SBT (Robertson 2010, não normalizada) e a SBTn (Robertson 2009)
 * pelas cores do CPeT-IT, os tokens `--ts-sbtn-1…9` do motor (o SBT usa as mesmas zonas).
 */
export const GRANDEZAS_DA_SOBREPOSICAO: readonly DefinicaoDaSobreposicao[] = Object.freeze([
  { chave: 'qc', rotulo: 'qc', unidade: 'MPa', tipo: 'serie', coluna: 'qc' },
  { chave: 'qt', rotulo: 'qt', unidade: 'MPa', tipo: 'serie', coluna: 'qt' },
  { chave: 'fs', rotulo: 'fs', unidade: 'kPa', tipo: 'serie', coluna: 'fs' },
  { chave: 'u2', rotulo: 'u₂', unidade: 'kPa', tipo: 'serie', coluna: 'u2' },
  { chave: 'rf', rotulo: 'Rf', unidade: '%', tipo: 'serie', coluna: 'rf' },
  { chave: 'ic', rotulo: 'Ic', unidade: '', tipo: 'serie', coluna: 'ic', dominio: [1, 4] },
  { chave: 'qtn', rotulo: 'Qtn', unidade: '', tipo: 'serie', coluna: 'qtn' },
  { chave: 'su', rotulo: 'Su', unidade: 'kPa', tipo: 'serie', coluna: 'su' },
  { chave: 'sbt', rotulo: 'SBT (Robertson 2010)', unidade: '', tipo: 'zona', coluna: 'sbt' },
  { chave: 'sbtn', rotulo: 'SBTn (Robertson 2009)', unidade: '', tipo: 'zona', coluna: 'sbtn' },
].map((g) => Object.freeze(g) as DefinicaoDaSobreposicao));

/** A definição de uma grandeza da sobreposição. */
export function grandezaDaSobreposicao(chave: GrandezaDaSobreposicao): DefinicaoDaSobreposicao {
  return GRANDEZAS_DA_SOBREPOSICAO.find((g) => g.chave === chave) ?? GRANDEZAS_DA_SOBREPOSICAO[1];
}

/** A largura da faixa de cada vertical sobre a seção, px — ESCOLHA DA TELA, SEM FONTE [esp.sobreposicao-largura]. */
export const LARGURA_DA_SOBREPOSICAO = 48;
/** A opacidade de partida das faixas (0 a 1) — ESCOLHA DA TELA, SEM FONTE [esp.sobreposicao-opacidade]. */
export const OPACIDADE_DA_SOBREPOSICAO = 0.7;
/** A escala de partida das faixas: a mesma em todas — ESCOLHA DA TELA, SEM FONTE [esp.sobreposicao-escala]. */
export const ESCALA_DA_SOBREPOSICAO: 'comum' | 'propria' = 'comum';

/** Um trecho da coluna das zonas, em cota (topo ≥ base). */
export interface TrechoDeZona { topo: number; base: number; zona: number }

/** A faixa de uma vertical: o perfil medido em cota (ou os trechos das zonas) e a escala dela. */
export interface SobreposicaoDaVertical {
  codigo: string;
  latitude: number | null;
  longitude: number | null;
  /** A cota da boca, m. */
  cota: number;
  /** A cota de cada leitura (boca − profundidade), de cima para baixo. */
  cotas: Float64Array;
  /** O valor medido de cada leitura (NaN sem valor); na zona, o número da zona (0 sem classe). */
  valores: Float64Array;
  /** Os trechos contíguos da mesma zona (só na zona; [] na série). */
  trechos: TrechoDeZona[];
  /** O domínio da escala horizontal da faixa (na zona, [0, 1]). */
  dominio: [number, number];
  /** A marca de homologação da coluna nesta vertical (as premissas dela). */
  marca: MarcaDeHomologacao;
}

export interface SobreposicoesDasVerticais {
  grandeza: DefinicaoDaSobreposicao;
  escala: 'comum' | 'propria';
  /**
   * O tipo do eixo horizontal das faixas (onda 347, R12): linear, ou log quando a tela escolheu — na zona, linear. No
   * log, o valor ≤ 0 não tem lugar (a faixa e a fita abrem uma falha).
   */
  tipo: EscalaDoEixo;
  /** Os limites fixos que a tela escolheu (valem para todas, com «a escala de cada vertical» também); null: automáticos. */
  fixos: { min: number; max: number } | null;
  /** Na ordem pedida (`codigos`), ou na das verticais. */
  verticais: SobreposicaoDaVertical[];
  /** As pedidas que não entram, com o porquê (sem cota não há onde pôr a faixa). */
  fora: { codigo: string; motivo: 'sem cota' }[];
  /** As zonas que aparecem nas faixas (na zona), em ordem. */
  zonasPresentes: number[];
  /** A marca combinada das verticais (a pior delas, com o porquê). */
  marca: MarcaDeHomologacao;
}

/**
 * Os trechos de zona de uma vertical: a zona da leitura i vale do meio do caminho à leitura de cima ao meio do caminho
 * à de baixo (as pontas, até a própria leitura) — ESCOLHA DA TELA, SEM FONTE [esp.sobreposicao-trecho]; trechos
 * contíguos da mesma zona se juntam; a zona 0 (sem classe) deixa o trecho em branco.
 */
export function trechosDeZona(cotaDaBoca: number, z: ArrayLike<number>, zonas: ArrayLike<number>): TrechoDeZona[] {
  const saida: TrechoDeZona[] = [];
  const n = Math.min(z.length, zonas.length);
  for (let i = 0; i < n; i++) {
    const zona = zonas[i];
    if (!(zona > 0) || !Number.isFinite(z[i])) continue;
    const de = i > 0 && Number.isFinite(z[i - 1]) ? (z[i - 1] + z[i]) / 2 : z[i];
    const ate = i + 1 < n && Number.isFinite(z[i + 1]) ? (z[i] + z[i + 1]) / 2 : z[i];
    const topo = cotaDaBoca - de;
    const base = cotaDaBoca - ate;
    const ultimo = saida[saida.length - 1];
    if (ultimo && ultimo.zona === zona && Math.abs(ultimo.base - topo) <= FOLGA) ultimo.base = base;
    else saida.push({ topo, base, zona });
  }
  return saida;
}

/**
 * As faixas das verticais para sobrepor à seção (e ao 3D): de cada vertical pedida em `codigos` (todas, sem ele), o
 * perfil MEDIDO da grandeza em cota — a vertical como ela é, nunca o estimado —, ou os trechos da zona SBT/SBTn do
 * classificador do motor (`r.sbt`, `r.sbtn`). A escala horizontal de cada faixa é a mesma em todas (`comum`, de
 * partida) ou a de cada vertical (`propria`); o Ic vai sempre de 1 a 4. Pura: o 3D (316.3) e o PDF (316.5) a reusam.
 */
export function sobreposicoesDasVerticais(
  vs: readonly VerticalDaAnalise[],
  x: {
    grandeza: GrandezaDaSobreposicao; codigos?: readonly string[]; escala?: 'comum' | 'propria';
    /**
     * A escala horizontal escolhida na tela (onda 347, R12; a `EscalaDoGrafico` do `escalaDoGrafico.ts`): o tipo e os
     * limites. Os limites fixos valem para todas as faixas; no automático, o domínio de hoje (com o zero no linear;
     * no log, só os valores > 0), comum ou de cada vertical. Sem ela — o PDF —, como antes.
     */
    eixo?: { tipo: EscalaDoEixo; limites: 'auto' | { min: number; max: number } } | null;
  },
): SobreposicoesDasVerticais {
  const def = grandezaDaSobreposicao(x.grandeza);
  const escala = x.escala ?? ESCALA_DA_SOBREPOSICAO;
  const tipo: EscalaDoEixo = def.tipo === 'serie' && x.eixo?.tipo === 'log' ? 'log' : 'linear';
  const fixos = def.tipo === 'serie' && x.eixo && x.eixo.limites !== 'auto'
    && Number.isFinite(x.eixo.limites.min) && Number.isFinite(x.eixo.limites.max) && x.eixo.limites.min < x.eixo.limites.max
    && (tipo === 'linear' || x.eixo.limites.min > 0)
    ? { min: x.eixo.limites.min, max: x.eixo.limites.max } : null;
  const porCodigo = new Map(vs.map((v) => [v.codigo, v]));
  const pedidas = x.codigos ? x.codigos.map((c) => porCodigo.get(c)).filter((v): v is VerticalDaAnalise => !!v) : [...vs];
  const unicas = pedidas.filter((v, i) => pedidas.findIndex((w) => w.codigo === v.codigo) === i);
  const fora = unicas.filter((v) => !temNumero(v.cota)).map((v) => ({ codigo: v.codigo, motivo: 'sem cota' as const }));
  const entram = unicas.filter((v) => temNumero(v.cota));

  const serieDa = (v: VerticalDaAnalise): Float64Array => {
    if (def.tipo === 'zona') return Float64Array.from(def.chave === 'sbt' ? v.r.sbt : v.r.sbtn);
    const s = v.r.col[def.coluna as Grandeza];
    return s ? Float64Array.from(s) : new Float64Array(v.r.n).fill(NaN);
  };
  const dominioDa = (series: readonly Float64Array[]): [number, number] => {
    if (def.tipo === 'zona') return [0, 1];
    if (fixos) return [fixos.min, fixos.max];
    if (def.dominio && tipo === 'linear') return [def.dominio[0], def.dominio[1]];
    return dominioDe(series, tipo, { comZero: tipo === 'linear' });
  };

  const dados = entram.map((v) => ({ v, valores: serieDa(v) }));
  const comum = dominioDa(dados.map((d) => d.valores));
  const presentes = new Set<number>();
  const verticais: SobreposicaoDaVertical[] = dados.map(({ v, valores }) => {
    const cota = v.cota as number;
    const cotas = Float64Array.from(v.r.col.z, (z) => cota - z);
    const trechos = def.tipo === 'zona' ? trechosDeZona(cota, v.r.col.z, valores) : [];
    for (const t of trechos) presentes.add(t.zona);
    return {
      codigo: v.codigo, latitude: v.latitude, longitude: v.longitude, cota, cotas, valores, trechos,
      dominio: escala === 'propria' ? dominioDa([valores]) : comum,
      marca: marcaDaColuna(def.coluna, v.r.premissas),
    };
  });
  const marca = verticais.length
    ? combinarMarcas(verticais.map((s) => ({ marca: s.marca })))
    : { mostrar: true, homologada: true, motivo: null };
  return { grandeza: def, escala, tipo, fixos, verticais, fora, zonasPresentes: [...presentes].sort((a, b) => a - b), marca };
}
